// UI strings. French is the reference: the English dictionary must have exactly the same keys.

export const fr = {
  appName: 'Syndiqia',
  loading: 'Chargement…',
  signIn: 'Se connecter',
  signOut: 'Se déconnecter',
  signingIn: 'Connexion en cours…',
  signInError: 'La connexion a échoué.',
  retry: 'Réessayer',
  language: 'English',
  languageShort: 'EN',
  about: 'Découvrir Syndiqia',
  aboutShort: 'Découvrir',
  salesTools: {
    title: 'Outils de vente',
    intro: 'Tout ce qu’il faut pour vendre Syndiqia : les démos, les outils d’appel, les présentations et les pages à montrer aux clients. Réservé à l’équipe Syndiqia.',
    internal: 'Interne',
    open: 'Ouvrir →',
    download: (format: string) => `Télécharger (${format})`,
    downloadFailed: 'Téléchargement impossible : réessayez',
  },
  console: {
    title: 'Console Syndiqia',
    onboardingTag: 'Mise en service',
    onboardAssociation: 'Mettre en service une association',
    onboardSyndicate: 'Mettre en service un syndicat',
    newClientName: 'Nom du client',
    newClientNameHint: 'Tel qu’il figure aux lettres patentes ou à la déclaration',
    newClientSlug: 'Adresse dans Syndiqia',
    newClientSlugHint: 'Lettres minuscules, chiffres et tirets; sert aussi aux courriels (demandes@…)',
    startOnboarding: 'Commencer la mise en service',
    onboardingStatus: (done: number, total: number) => `Mise en service · ${done} / ${total}`,
    continueOnboarding: 'Continuer',
    activeStatus: 'Actif',
    intro: (roles: string) => `Espace de l’équipe Syndiqia — vos rôles : ${roles}. Vous n’avez accès aux données d’un vrai client que pendant sa mise en service, ou ensuite si un de ses administrateurs l’a approuvé.`,
    roles: { platform_admin: 'Administrateur de la plateforme', sales: 'Ventes', support: 'Soutien' },
    demos: 'Démonstrations',
    demosHint: 'Données fictives. Vous y agissez comme administrateur; aucun courriel n’atteint de vraies personnes.',
    demoBadge: 'Démo',
    own: 'Vos regroupements',
    supportOpen: 'Accès de soutien en cours',
    supportBadge: 'Lecture seule',
    expires: (when: string) => `Expire le ${when}`,
    clients: 'Clients',
    clientsHint: 'Renseignements de base seulement : aucune donnée personnelle n’est visible ici.',
    noClients: 'Aucun client pour le moment.',
    client: 'Client',
    kind: 'Type',
    size: 'Taille',
    since: 'Depuis',
    units: (n: number) => (n === 1 ? '1 unité' : `${n} unités`),
    askAccess: 'Demander un accès de soutien',
    reason: 'Motif',
    reasonHint: 'Visible par les administrateurs du client',
    hours: 'Durée',
    hoursOption: (h: number) => (h < 24 ? `${h} heures` : h === 24 ? '24 heures' : h === 168 ? '7 jours' : `${h / 24} jours`),
    send: 'Envoyer la demande',
    myRequests: 'Vos demandes d’accès',
    statuses: { requested: 'En attente', approved: 'Approuvée', rejected: 'Refusée', revoked: 'Retirée', expired: 'Expirée' },
    team: 'Équipe Syndiqia',
    addMember: 'Ajouter un membre',
    person: 'Personne',
    rolesHeading: 'Rôles',
    edit: 'Modifier',
    roleHelp: {
      platform_admin: 'Gère l’équipe et les clients; ouvre les démonstrations.',
      sales: 'Ouvre et présente les démonstrations.',
      support: 'Demande un accès temporaire, en lecture seule, aux données d’un client qui l’approuve.',
    },
    leaveWarning: 'Sans aucun rôle, la personne quitte l’équipe Syndiqia et n’a plus accès à la console.',
    confirmLeave: (who: string) => `Retirer ${who} de l’équipe Syndiqia?`,
    demoTag: 'Démo',
    viewAs: 'Voir comme',
    personas: { director: 'Administrateur (vous)', manager: 'Gestionnaire', owner: 'Copropriétaire', occupant: 'Occupant' },
    reset: 'Réinitialiser la démo',
    resetting: 'Réinitialisation en cours : la démo sera prête dans une minute environ.',
    confirmReset: (name: string) => `Réinitialiser « ${name} »? Toutes les modifications faites dans cette démo seront effacées.`,
    email: 'Courriel de connexion',
    emailHint: 'Si la personne n’a pas encore de compte, elle reçoit un courriel pour choisir son mot de passe',
    invitationNotSent: (email: string) =>
      `Le compte de ${email} est créé et son rôle ajouté, mais le courriel pour choisir son mot de passe n’a pas pu être envoyé. Tant que l’envoi de courriels de Syndiqia est en mode test chez Amazon (SES), seules les adresses vérifiées le reçoivent : faites vérifier l’adresse, puis renvoyez l’invitation.`,
    resendInvitation: 'Renvoyer l’invitation',
    invitationPending: 'Invitation en attente',
    memberName: 'Nom',
    memberNameHint: 'Pour une nouvelle personne : le nom affiché dans Syndiqia',
    invitationSent: (email: string) => `Invitation envoyée à ${email} : la personne choisit son mot de passe à partir du courriel reçu.`,
    role: 'Rôle',
    grant: 'Ajouter le rôle',
    remove: 'Retirer',
    confirmRemove: (role: string, who: string) => `Retirer le rôle « ${role} » à ${who}?`,
    bannerDemo: 'Mode démonstration : données fictives, vous agissez comme administrateur.',
    bannerSupport: (until: string) => `Accès de soutien Syndiqia en lecture seule, approuvé par le conseil${until ? ` jusqu’au ${until}` : ''}. Chaque page consultée est consignée.`,
    back: 'Retour à la console',
    panelTitle: 'Accès du soutien Syndiqia',
    panelIntro: 'Le soutien Syndiqia n’a accès à vos données que si un administrateur l’approuve : temporairement, en lecture seule, et chaque page consultée est consignée. Vous pouvez retirer l’accès à tout moment.',
    panelNone: 'Aucune demande.',
    panelAsked: (d: string, h: number) => `Demandée le ${d}, pour ${h} h`,
    panelViews: (n: number, last: string) => `${n} page${n > 1 ? 's' : ''} consultée${n > 1 ? 's' : ''}${last ? `, dernière le ${last}` : ''}`,
    approve: 'Approuver',
    reject: 'Refuser',
    revoke: 'Retirer l’accès',
    confirmApprove: (h: number) => `Donner au soutien Syndiqia un accès en lecture seule pendant ${h} heures?`,
  },
  manual: {
    title: 'Guide d’utilisation',
    back: 'Retour à l’application',
    print: 'Imprimer',
    contents: 'Table des matières',
    introduction: 'Introduction',
    roles: 'Les rôles',
    role: 'Rôle',
    canDo: 'Ce que la personne peut faire',
    glossary: 'Glossaire',
    everyone: 'Tous',
    board: 'Conseil',
    version: 'Version du guide',
  },
  noSyndicate: "Votre compte n'est rattaché à aucun syndicat. Communiquez avec votre conseil d'administration.",
  chooseSyndicate: 'Choisissez un regroupement',
  chooseIntro: 'Votre compte donne accès à plusieurs syndicats ou associations.',
  chooseOpen: 'Ouvrir',
  chooseKinds: { divided_coownership: 'Syndicat de copropriétaires', npo_with_servitudes: 'Association de propriétaires' },
  roleLabel: (n: number): string => (n > 1 ? 'Rôles : ' : 'Rôle : '),
  roles: {
    director: "Membre du conseil d'administration",
    manager: 'Gestionnaire',
    owner: 'Copropriétaire',
    occupant: 'Occupant',
    vendor: 'Fournisseur',
    support: 'Soutien Syndiqia (lecture seule)',
  },
  maintenance: {
    title: 'Entretien',
    boardOnly: 'L’entretien est géré par le conseil. Pour signaler un problème, faites une demande.',
    nav: { workOrders: 'Bons de travail', calendar: 'Calendrier', logbook: 'Carnet d’entretien', assets: 'Composantes', vendors: 'Fournisseurs' },
    trades: {
      general: 'Entrepreneur général', plumbing: 'Plomberie', electrical: 'Électricité', hvac: 'Chauffage et ventilation', roofing: 'Toiture',
      elevator: 'Ascenseur', fire_safety: 'Sécurité incendie', envelope: 'Enveloppe (façade, fenêtres)', landscaping: 'Aménagement paysager',
      snow_removal: 'Déneigement', cleaning: 'Entretien ménager', pest_control: 'Extermination', locksmith: 'Serrurerie', painting: 'Peinture',
      professional: 'Professionnel (ingénieur, architecte…)', other: 'Autre',
    },
    categories: {
      structure: 'Structure', envelope: 'Enveloppe (façade, fenêtres)', roofing: 'Toiture', plumbing: 'Plomberie', electrical: 'Électricité',
      hvac: 'Chauffage et ventilation', elevator: 'Ascenseur', fire_safety: 'Sécurité incendie', parking: 'Stationnement',
      exterior: 'Aménagements extérieurs', common_areas: 'Aires communes', other: 'Autre',
    },
    conditions: { good: 'Bon', fair: 'Moyen', poor: 'Mauvais', unknown: 'Inconnu' },
    statuses: { open: 'À attribuer', assigned: 'Attribué', scheduled: 'Planifié', completed: 'Terminé', cancelled: 'Annulé' },
    taskKinds: { legal: 'Obligation légale', insurance: 'Assurance', preventive: 'Entretien préventif' },
    taskStatuses: { overdue: 'En retard', due_soon: 'Bientôt', ok: 'À jour', unscheduled: 'À planifier', inactive: 'Désactivée' },
    logbookKinds: { inspection: 'Inspection', maintenance: 'Entretien', repair: 'Réparation', replacement: 'Remplacement', study: 'Étude ou rapport', correction: 'Correction', other: 'Autre' },
    urgent: 'Urgent',
    normal: 'Normale',
    number: (n: number): string => `Bon no ${n}`,
    newWorkOrder: 'Nouveau bon de travail',
    workOrderTitle: 'Titre',
    description: 'Description',
    priority: 'Priorité',
    asset: 'Composante',
    vendor: 'Fournisseur',
    scheduledOn: 'Date prévue',
    estimate: 'Estimation',
    actualCost: 'Coût réel',
    status: 'Statut',
    noWorkOrders: 'Aucun bon de travail en cours.',
    showAll: 'Afficher aussi les bons terminés',
    fromRequest: (n: number): string => `Demande no ${n}`,
    seeRequest: 'Voir la demande',
    complete: 'Marquer comme terminé',
    completedOn: 'Terminé le',
    completionNote: 'Ce qui a été fait',
    logbookKind: 'Type d’intervention (carnet)',
    completeHint: 'Une entrée sera ajoutée au carnet d’entretien.',
    cancelOrder: 'Annuler le bon',
    closed: 'Ce bon de travail est fermé.',
    emailVendor: 'Écrire au fournisseur',
    none: '—',
    // Calendar
    calendarIntro: 'Les obligations légales, les assurances et l’entretien préventif du syndicat, avec leurs échéances.',
    addStandard: 'Ajouter le calendrier de base',
    addStandardHint: 'Étude du fonds de prévoyance, carnet d’entretien, assurances, inspections de sécurité incendie… Vous ajusterez les dates ensuite.',
    standardAdded: (n: number): string => (n === 0 ? 'Le calendrier de base est déjà en place.' : `${n} obligations ajoutées.`),
    addTask: 'Ajouter une obligation',
    taskTitle: 'Obligation',
    kind: 'Type',
    every: 'Fréquence',
    everyMonths: (n: number | null): string => (n === null ? 'Une fois' : n % 12 === 0 ? (n === 12 ? 'Chaque année' : `Tous les ${n / 12} ans`) : `Tous les ${n} mois`),
    monthsLabel: 'Fréquence en mois',
    nextDue: 'Prochaine échéance',
    lastDone: 'Dernière fois',
    reference: 'Référence',
    daysLeft: (d: number): string => (d < 0 ? `En retard de ${-d} j` : d === 0 ? 'Aujourd’hui' : `Dans ${d} j`),
    markDone: 'Fait',
    doneOn: 'Fait le',
    doneNote: 'Note (rapport, entreprise…)',
    cost: 'Coût',
    setDate: 'Fixer la date',
    deactivate: 'Désactiver',
    reactivate: 'Réactiver',
    noTasks: 'Aucune obligation au calendrier.',
    groups: { overdue: 'En retard', due_soon: 'Dans les 60 prochains jours', unscheduled: 'À planifier', ok: 'À jour', inactive: 'Désactivées' },
    // Logbook
    logbookIntro: 'Le carnet d’entretien du syndicat : chaque inspection, réparation ou remplacement, dans l’ordre. Les entrées ne se modifient pas; une correction s’ajoute.',
    logbookProfessional: 'Le carnet officiel est établi et révisé par un membre d’un ordre professionnel; ce registre l’alimente.',
    addEntry: 'Ajouter une entrée',
    entryDate: 'Date',
    entryTitle: 'Intervention',
    allAssets: 'Toutes les composantes',
    noEntries: 'Aucune entrée au carnet.',
    recordedBy: (name: string): string => `Inscrit par ${name}`,
    print: 'Imprimer',
    // Assets
    assetsIntro: 'Les composantes des parties communes, leur âge et leur durée de vie : la base du carnet d’entretien et de l’étude du fonds de prévoyance.',
    addAsset: 'Ajouter une composante',
    assetName: 'Composante',
    category: 'Catégorie',
    location: 'Emplacement',
    installedOn: 'Installée le',
    lifeYears: 'Durée de vie (ans)',
    endOfLife: 'Fin de vie prévue',
    replacementCost: 'Coût de remplacement',
    condition: 'État',
    notes: 'Notes',
    noAssets: 'Aucune composante inscrite.',
    edit: 'Modifier',
    history: 'Historique',
    // Vendors
    vendorsIntro: 'Les fournisseurs du syndicat. Vérifiez la licence RBQ et l’attestation d’assurance avant d’attribuer des travaux.',
    addVendor: 'Ajouter un fournisseur',
    vendorName: 'Nom',
    trade: 'Spécialité',
    contactName: 'Personne-ressource',
    email: 'Courriel',
    phone: 'Téléphone',
    rbq: 'Licence RBQ',
    insuranceExpires: 'Assurance valide jusqu’au',
    insuranceExpired: 'Assurance expirée',
    insuranceSoon: 'Assurance à renouveler',
    active: 'Actif',
    inactive: 'Inactif',
    noVendors: 'Aucun fournisseur inscrit.',
    // Dashboard and requests
    dashboardTitle: 'Entretien et conformité',
    overdueTodo: (n: number): string => (n === 1 ? '1 obligation en retard' : `${n} obligations en retard`),
    dueSoonTodo: (n: number): string => (n === 1 ? '1 obligation à échéance dans les 60 jours' : `${n} obligations à échéance dans les 60 jours`),
    urgentTodo: (n: number): string => (n === 1 ? '1 bon de travail urgent' : `${n} bons de travail urgents`),
    vendorReportsTodo: (n: number): string => (n === 1 ? '1 bon de travail : réponse du fournisseur à traiter' : `${n} bons de travail : réponses des fournisseurs à traiter`),
    vendorInsuranceTodo: (n: number): string => (n === 1 ? '1 fournisseur dont l’assurance expire' : `${n} fournisseurs dont l’assurance expire`),
    calendarEmpty: 'Le calendrier des obligations n’est pas encore en place',
    openOrders: (n: number): string => (n === 1 ? '1 bon de travail en cours' : `${n} bons de travail en cours`),
    upToDate: 'Calendrier à jour.',
    requestOrders: 'Bons de travail',
    createFromRequest: 'Créer un bon de travail',
    proposalTitle: 'Bon de travail proposé',
    proposalHint: 'Préparé par l’agent à partir de la demande. Rien n’est créé ni envoyé sans vous.',
    useProposal: 'Créer ce bon de travail',
    dismissProposal: 'Écarter',
    proposalDismissed: 'Proposition de bon de travail écartée.',
    vendorMessage: 'Message proposé au fournisseur',
    copy: 'Copier',
    copied: 'Copié',
    why: 'Pourquoi',
    noVendorFound: 'Aucun fournisseur de ce métier dans votre liste',
  },
  governance: {
    title: 'Assemblées et réunions',
    intro: 'Assemblées des copropriétaires et réunions du conseil : ordre du jour, avis de convocation, notes, procès-verbal et suivis.',
    introNpo: 'Assemblées des membres et réunions du conseil : ordre du jour, avis de convocation, notes, procès-verbal et suivis.',
    boardOnly: 'Les assemblées sont préparées par le conseil. Les procès-verbaux approuvés sont dans Documents.',
    kinds: { annual: 'Assemblée annuelle', special: 'Assemblée extraordinaire', board: 'Réunion du conseil' },
    statuses: { draft: 'En préparation', convened: 'Convoquée', held: 'Tenue', closed: 'Terminée', cancelled: 'Annulée' },
    itemKinds: { procedure: 'Procédure', information: 'Information', discussion: 'Discussion', resolution: 'Résolution', election: 'Élection' },
    majorities: { simple: 'Majorité simple', absolute: 'Majorité absolue', double: 'Double majorité', triple: 'Triple majorité', unanimous: 'Unanimité' },
    sources: { statutory: 'Point usuel', request: 'Demande', obligation: 'Obligation', work_order: 'Bon de travail', follow_up: 'Suivi', proposal: 'Proposition', manual: 'Ajouté' },
    newMeeting: 'Nouvelle réunion',
    chair: 'Préside',
    signTodo: (title: string): string => `Résolution écrite à signer : ${title}`,
    makeChair: 'Présider',
    castingUsed: 'voix prépondérante',
    repeat: 'Répéter',
    repeats: { none: 'Une seule fois', week: 'Chaque semaine', two_weeks: 'Aux deux semaines', month: 'Chaque mois', two_months: 'Aux deux mois', quarter: 'Chaque trimestre' },
    count: 'Nombre de réunions',
    seriesPreview: (dates: string): string => `Dates : ${dates}`,
    seriesCreated: (n: number): string => `${n} réunions créées.`,
    sections: { meetings: 'Réunions', written: 'Résolutions écrites', registers: 'Registres' },
    written: {
      intro: 'Une résolution écrite, signée par tous les administrateurs, a la même valeur que si elle avait été adoptée en réunion du conseil (C.c.Q., art. 337; à vérifier selon vos règlements).',
      new: 'Nouvelle résolution écrite',
      title: 'Titre',
      text: 'Texte de la résolution',
      textPlaceholder: 'Il est résolu que…',
      none: 'Aucune résolution écrite.',
      statuses: { draft: 'Brouillon', circulating: 'En signature', adopted: 'Adoptée', cancelled: 'Annulée' },
      circulate: 'Envoyer aux administrateurs pour signature',
      confirmCirculate: 'Envoyer cette résolution à tous les administrateurs en fonction? Le texte ne pourra plus être modifié.',
      sign: 'Je signe cette résolution',
      confirmSign: 'Signer cette résolution? Votre signature est datée et ne peut pas être retirée.',
      cancel: 'Annuler la résolution',
      confirmCancel: 'Annuler cette résolution?',
      signed: (d: string): string => `Signé le ${d}`,
      waiting: 'En attente',
      adoptedOn: (d: string): string => `Adoptée le ${d}`,
      progress: (n: number, total: number): string => `${n} signature${n > 1 ? 's' : ''} sur ${total}`,
      yourTurn: 'Votre signature est attendue.',
    },
    registers: {
      intro: 'Le registre des résolutions et le livre des procès-verbaux du syndicat, tenus à jour à partir des réunions et des résolutions écrites.',
      resolutions: 'Registre des résolutions',
      scope: { board: 'Conseil d’administration', assembly: 'Assemblées' },
      number: 'No',
      date: 'Date',
      source: 'Origine',
      resolution: 'Résolution',
      decision: 'Décision',
      written: 'Résolution écrite',
      none: 'Aucune résolution au registre.',
      minuteBook: 'Livre des procès-verbaux',
      minuteBookNone: 'Aucun procès-verbal approuvé.',
      open: 'Ouvrir',
      print: 'Imprimer',
      rejected: 'Rejetée',
    },
    kind: 'Type',
    meetingTitle: 'Titre',
    date: 'Date',
    time: 'Heure',
    location: 'Lieu',
    upcoming: 'À venir',
    past: 'Passées',
    none: 'Aucune réunion.',
    back: 'Toutes les réunions',
    steps: { agenda: 'Ordre du jour', notice: 'Avis de convocation', meeting: 'Réunion', minutes: 'Procès-verbal' },
    // Agenda
    agendaHint: 'Les points usuels sont déjà en place. Ajoutez les sujets de cette réunion, ou laissez l’agent les proposer.',
    addItem: 'Ajouter un point',
    itemTitle: 'Point',
    description: 'Contexte',
    itemKind: 'Nature',
    resolution: 'Proposition de résolution',
    majority: 'Majorité requise',
    up: 'Monter',
    down: 'Descendre',
    remove: 'Retirer',
    edit: 'Modifier',
    draftAgenda: 'Préparer avec l’agent',
    draftingAgenda: 'L’agent examine les demandes, les échéances, les travaux et les suivis…',
    proposalsTitle: 'Points proposés par l’agent',
    proposalsHint: 'À partir de ce qui est en attente ailleurs dans Syndiqia. Ajoutez ceux que vous retenez.',
    noProposals: 'Rien en attente qui devrait s’ajouter à cet ordre du jour.',
    add: 'Ajouter',
    added: 'Ajouté',
    convenedEdit: 'L’avis a été envoyé avec cet ordre du jour : les changements devront être annoncés en séance.',
    // Notice
    noticeRule: (min: number | null, max: number | null): string =>
      min === null ? 'Pas de délai de convocation imposé.' : max === null ? `À envoyer au moins ${min} jours avant la réunion.` : `À envoyer entre ${min} et ${max} jours avant la réunion.`,
    noticeDays: (d: number): string => (d < 0 ? 'La date de la réunion est passée.' : `La réunion est dans ${d} jour${d > 1 ? 's' : ''}.`),
    outsidePeriod: 'Hors du délai habituel : vérifiez la déclaration ou les règlements.',
    ruleToValidate: 'Délais par défaut, à valider selon la déclaration de copropriété ou les règlements.',
    prepareNotice: 'Préparer l’avis de convocation',
    noticeRecipients: (n: number): string => `${n} destinataire${n > 1 ? 's' : ''}`,
    noticeUnreachable: (n: number): string => `${n} personne${n > 1 ? 's' : ''} sans courriel : à convoquer autrement (et conserver la preuve).`,
    noticePending: 'Avis prêt, en attente d’approbation par un administrateur.',
    approveSend: (n: number): string => `Approuver et envoyer (${n} courriel${n > 1 ? 's' : ''})`,
    reject: 'Refuser',
    noticeSent: (n: number, date: string): string => `Avis envoyé le ${date} à ${n} destinataire${n > 1 ? 's' : ''}, avec preuve de remise.`,
    noticeRejected: 'L’avis a été refusé. Modifiez l’ordre du jour puis préparez-le de nouveau.',
    preview: 'Aperçu du courriel',
    markConvened: 'Convoquée autrement',
    // Meeting
    generalNotes: 'Présences, quorum, présidence et secrétariat',
    generalNotesHint: 'Ex. : 18 copropriétaires présents ou représentés (62 % des voix); présidente : …; secrétaire : …',
    notes: 'Notes',
    decision: 'Décision',
    decisionPlaceholder: 'Ex. : Adoptée à l’unanimité',
    saved: 'Enregistré',
    markHeld: 'Marquer la réunion comme tenue',
    cancelMeeting: 'Annuler la réunion',
    // Minutes
    draftMinutes: 'Rédiger le procès-verbal avec l’agent',
    redraftMinutes: 'Rédiger de nouveau avec l’agent',
    draftingMinutes: 'L’agent rédige le procès-verbal à partir de vos notes…',
    minutesHint: 'L’agent n’invente rien : ce qui manque est marqué [À compléter]. Relisez, corrigez, puis approuvez.',
    minutesText: 'Procès-verbal',
    saveMinutes: 'Enregistrer',
    approveMinutes: 'Approuver et publier dans Documents',
    approvedOn: (date: string): string => `Approuvé le ${date}.`,
    publishedNote: 'Publié dans Documents.',
    seeDocument: 'Voir le document',
    // Actions
    actions: 'Suivis',
    actionsHint: 'Les suivis ouverts sont proposés à l’ordre du jour de la prochaine réunion.',
    addAction: 'Ajouter un suivi',
    actionDescription: 'Suivi',
    responsible: 'Responsable',
    dueOn: 'Échéance',
    done: 'Fait',
    reopen: 'Rouvrir',
    noActions: 'Aucun suivi.',
    openActions: 'Suivis ouverts',
    // Dashboard
    noticeTodo: (title: string): string => `Avis de convocation à approuver : ${title}`,
    minutesTodo: (title: string): string => `Procès-verbal à approuver : ${title}`,
    upcomingTodo: (title: string, days: number): string => `${title} dans ${days} jour${days > 1 ? 's' : ''} : avis de convocation à envoyer`,
    aiDisabled: 'Les agents ne sont pas activés.',
    // Attendance and votes
    attendance: 'Feuille de présence',
    attendanceHint: 'Préparée à partir du registre à la date de la réunion : chaque copropriétaire une fois, avec ses voix (quotes-parts).',
    attendanceHintLots: 'Préparée à partir du registre à la date de la réunion : chaque propriétaire une fois, une voix par lot.',
    attendanceHintPersons: 'Préparée à partir du registre à la date de la réunion : chaque membre une fois, une voix chacun.',
    attendanceHintBoard: 'Les administrateurs, une voix chacun.',
    prepareAttendance: 'Préparer la feuille de présence',
    refreshAttendance: 'Préparer de nouveau',
    present: 'Présent',
    proxy: 'Procuration',
    absent: 'Absent',
    proxyHolder: 'Mandataire',
    votesLabel: 'Voix',
    noVote: 'Sans droit de vote',
    search: 'Rechercher un nom ou une unité',
    quorumLine: (pct: string, present: number, total: number): string => `${present} sur ${total} présents ou représentés · ${pct} des voix`,
    quorumReached: 'Quorum atteint',
    quorumNotReached: 'Quorum non atteint',
    allPresent: 'Tous présents',
    vote: 'Vote',
    unanimous: 'Unanimité',
    detailedVote: 'Vote détaillé',
    allFor: 'Tous pour',
    for: 'Pour',
    against: 'Contre',
    abstain: 'Abst.',
    tally: (forPct: string, againstPct: string, abstainPct: string): string => `Pour ${forPct} · Contre ${againstPct} · Abstentions ${abstainPct}`,
    eligibleLine: (p: string): string => `voix présentes ou représentées habilitées à voter : ${p}`,
    wouldPass: 'Serait adoptée',
    wouldFail: 'Serait rejetée',
    closeVote: 'Clore le vote',
    voteClosed: 'Vote clos',
    needAttendance: 'Préparez la feuille de présence pour enregistrer les votes.',
    rulesToValidate: 'Calcul des majorités selon le Code civil (art. 1089, 1096 à 1098), à valider selon la déclaration.',
    // Members
    memberTitle: 'Assemblées',
    memberIntro: 'Les assemblées auxquelles vous êtes convoqué, avec leur ordre du jour. Les procès-verbaux approuvés sont dans Documents.',
    memberNone: 'Aucune assemblée convoquée pour le moment.',
    minutesInDocuments: 'Procès-verbal disponible dans Documents',
    // Recording
    recording: 'Enregistrement de la séance',
    recordingHint: 'L’enregistrement est transcrit automatiquement pour rédiger le procès-verbal. L’audio est supprimé dès la transcription, et la transcription une fois le procès-verbal approuvé.',
    consent: 'À lire en ouverture : « La séance est enregistrée afin de faciliter la rédaction du procès-verbal. L’enregistrement sera supprimé une fois transcrit. Si quelqu’un s’y oppose, nous pouvons interrompre l’enregistrement. »',
    startRecording: 'Démarrer l’enregistrement',
    pause: 'Pause',
    resume: 'Reprendre',
    nextItem: 'Point suivant',
    finishRecording: 'Terminer l’enregistrement',
    recordingNow: (item: string): string => `En cours · ${item}`,
    pausedNow: 'En pause',
    uploaded: (mb: string): string => `${mb} Mo envoyés`,
    uploadRetry: 'Envoi interrompu, nouvel essai…',
    micDenied: 'Le micro n’est pas accessible. Autorisez-le dans le navigateur.',
    notSupported: 'Ce navigateur ne permet pas d’enregistrer. Utilisez Chrome, Edge, Firefox ou Safari récent.',
    leaveWarning: 'Un enregistrement est en cours.',
    importRecording: 'Importer un enregistrement ou une transcription',
    importHint: 'Fichier audio (MP3, M4A, WAV, WebM) d’une réunion Teams, Zoom ou d’un enregistreur, ou la transcription produite par Teams ou Zoom (.vtt, .txt).',
    importing: 'Envoi…',
    recordingStatuses: { recording: 'En cours', uploaded: 'En attente de transcription', transcribing: 'Transcription en cours…', transcribed: 'Transcrit', failed: 'Échec', purged: 'Supprimé' },
    recordingSources: { browser: 'Enregistré ici', upload: 'Fichier importé', transcript: 'Transcription importée' },
    retry: 'Relancer',
    transcript: 'Transcription',
    speakers: 'Intervenants',
    speakersHint: 'Donnez un nom ou un rôle à chaque voix (ex. : Présidente). Ils seront repris dans le procès-verbal.',
    speakerLabel: (n: number): string => `Intervenant ${n}`,
    duration: (min: number): string => `${min} min`,
    transcriptUsed: 'La transcription sera utilisée pour rédiger le procès-verbal.',
  },
  reservations: {
    title: 'Réservations',
    intro: 'Réservez les installations communes pour votre unité. Les règles de chaque installation s’appliquent automatiquement.',
    kinds: { room: 'Salle', gym: 'Gym', court: 'Terrain', elevator: 'Ascenseur', pool: 'Piscine', bbq: 'BBQ', guest_suite: 'Suite d’invités', other: 'Autre' },
    statuses: { requested: 'En attente d’approbation', confirmed: 'Confirmée', cancelled: 'Annulée', rejected: 'Refusée' },
    tabs: { book: 'Réserver', mine: 'Mes réservations', all: 'Toutes les réservations', facilities: 'Installations' },
    noFacilities: 'Aucune installation à réserver pour le moment.',
    hours: (from: string, to: string): string => `De ${from} à ${to}`,
    duration: (min: number, max: number): string => `${min < 60 ? `${min} min` : `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60}` : ''}`} à ${max < 60 ? `${max} min` : `${Math.floor(max / 60)} h${max % 60 ? ` ${max % 60}` : ''}`}`,
    fee: (amount: string): string => `Frais : ${amount}`,
    free: 'Gratuit',
    deposit: (amount: string): string => `Dépôt : ${amount}`,
    quota: (n: number): string => `${n} réservation${n > 1 ? 's' : ''} par semaine et par unité`,
    approval: 'Approbation du conseil requise',
    cancelRule: (h: number): string => `Annulation sans frais jusqu’à ${h} h avant`,
    ahead: (d: number): string => `Jusqu’à ${d} jours à l’avance`,
    day: 'Date',
    start: 'Début',
    length: 'Durée',
    unit: 'Unité',
    notes: 'Note (facultatif)',
    book: 'Réserver',
    bookFee: (amount: string): string => `Réserver (${amount} porté à votre compte)`,
    request: 'Demander la réservation',
    taken: 'Réservé',
    yours: 'Vous',
    closed: 'Fermé',
    free_: 'Libre',
    booked: 'Réservation confirmée.',
    requested: 'Demande envoyée au conseil.',
    rules: {
      order: 'La fin doit suivre le début.',
      same_day: 'La réservation doit commencer et finir le même jour.',
      hours: 'En dehors des heures d’ouverture.',
      slot: 'Choisissez une heure de début et une durée proposées.',
      too_short: 'Durée trop courte.',
      too_long: 'Durée trop longue.',
      notice: 'Délai trop court.',
      too_far: 'Trop longtemps à l’avance.',
      blackout: 'L’installation est fermée à ce moment.',
      quota: 'Limite de réservations de la semaine atteinte pour cette unité.',
      overlap: 'Ce créneau est déjà réservé.',
    },
    noUnit: 'Aucune unité associée à votre compte : demandez au conseil de vous relier à votre unité.',
    upcoming: 'À venir',
    none: 'Aucune réservation à venir.',
    cancel: 'Annuler',
    cancelUntil: (when: string): string => `Annulation sans frais jusqu’au ${when}`,
    cancelLate: 'Délai d’annulation dépassé : communiquez avec le conseil.',
    approve: 'Approuver',
    reject: 'Refuser',
    bookedBy: (name: string): string => `par ${name}`,
    // Facilities (board)
    addFacility: 'Ajouter une installation',
    edit: 'Modifier',
    name: 'Nom',
    kind: 'Type',
    description: 'Description',
    location: 'Emplacement',
    opensAt: 'Ouverture',
    closesAt: 'Fermeture',
    slotMinutes: 'Pas (minutes)',
    minMinutes: 'Durée minimale (min)',
    maxMinutes: 'Durée maximale (min)',
    maxDaysAhead: 'Jours à l’avance (max)',
    minNoticeHours: 'Délai minimal (heures)',
    cancelNoticeHours: 'Annulation sans frais (heures avant)',
    weeklyQuota: 'Réservations par semaine et par unité',
    feeLabel: 'Frais',
    depositLabel: 'Dépôt',
    requiresApproval: 'Approbation du conseil requise',
    rulesText: 'Règlement affiché',
    active: 'Disponible à la réservation',
    inactive: 'Non disponible',
    blackouts: 'Fermetures',
    addBlackout: 'Ajouter une fermeture',
    from: 'Du',
    to: 'Au',
    reason: 'Motif',
    remove: 'Retirer',
    requestsTodo: (n: number): string => (n === 1 ? '1 réservation à approuver' : `${n} réservations à approuver`),
  },
  members: {
    back: 'Copropriétaires et contacts',
    details: 'Coordonnées',
    name: 'Nom',
    email: 'Courriel',
    phone: 'Téléphone',
    language: 'Langue',
    mailingAddress: 'Adresse postale',
    edit: 'Modifier',
    saved: 'Enregistré.',
    contactNote: 'Le courriel sert aux avis. Le courriel de connexion reste celui du compte.',
    units: 'Unités',
    owned: 'Propriétaire',
    occupied: 'Occupant',
    share: (n: number, d: number): string => (n === d ? '' : ` (${n}/${d})`),
    since: (d: string): string => `depuis le ${d}`,
    period: (from: string, to: string): string => `du ${from} au ${to}`,
    noUnits: 'Aucune unité.',
    access: 'Accès et rôles',
    noAccount: 'Pas encore de compte.',
    roles: 'Rôles dans ce syndicat',
    makeDirector: 'Nommer administrateur',
    removeDirector: 'Retirer du conseil',
    makeManager: 'Nommer gestionnaire',
    removeManager: 'Retirer le rôle de gestionnaire',
    roleNote: 'Note (ex. : élu à l’AGA du 25 mars 2026)',
    confirmRole: (name: string, role: string, granted: boolean): string => (granted ? `Donner le rôle « ${role} » à ${name}?` : `Retirer le rôle « ${role} » à ${name}?`),
    inviteFirst: 'Invitez d’abord la personne : les rôles se donnent à un compte.',
    history: 'Historique des rôles',
    granted: 'nommé',
    removed: 'retiré',
    by: (name: string): string => `par ${name}`,
    // Occupants
    occupants: 'Occupants (locataires)',
    noOccupants: 'Aucun occupant inscrit.',
    addOccupant: 'Ajouter un occupant',
    person: 'Personne',
    from: 'À partir du',
    endOccupancy: 'Fin de l’occupation',
    endOn: 'Dernier jour',
    occupantHint: 'Un occupant peut faire des demandes et réserver pour l’unité; il ne voit pas le compte du propriétaire.',
    // Profile
    profileTitle: 'Mon profil',
    profileIntro: 'Vos coordonnées dans ce syndicat. Le conseil s’en sert pour les avis et les communications.',
    noRecord: 'Votre compte n’est pas encore relié à une fiche de ce syndicat. Demandez au conseil.',
    myUnits: 'Mes unités',
    digestTitle: 'Résumé par courriel',
    digestIntro: 'Ce que les agents ont fait seuls et ce qui attend le conseil, envoyé seulement s’il y a quelque chose.',
    digestOptions: { daily: 'Chaque jour', weekly: 'Chaque lundi', off: 'Jamais' },
    loginEmail: 'Courriel de connexion',
    loginEmailIntro: (email: string) => `Vous vous connectez avec ${email}. Pour le changer, entrez la nouvelle adresse : un lien de confirmation y sera envoyé.`,
    newLoginEmail: 'Nouvelle adresse',
    changeLoginEmail: 'Changer',
    loginEmailPending: (email: string) => `Un lien de confirmation a été envoyé à ${email}. Utilisez cette adresse pour vous connecter à l’avenir.`,
    loginEmailHint: 'C’est l’adresse de votre compte (pour tous vos syndicats); vos coordonnées ci-dessus peuvent être différentes.',
  },
  help: {
    title: 'Aide',
    short: '?',
    intro: 'Le guide d’utilisation de Syndiqia : les tâches qui vous concernent, selon votre rôle.',
    search: 'Rechercher dans le guide…',
    results: 'Résultats',
    noResults: 'Aucun sujet trouvé. Essayez d’autres mots, ou posez la question sur l’accueil.',
    onThisPage: (page: string) => `Sur cette page : ${page}`,
    askHint: 'Vous ne trouvez pas? Posez votre question sur l’accueil : la réponse vient de ce guide et des documents du syndicat.',
    askLink: 'Aller à l’accueil',
    guide: 'Guide Syndiqia',
    pages: {
      start: 'Pour commencer', home: 'Accueil', profile: 'Mon profil', requests: 'Demandes', documents: 'Documents', units: 'Unités',
      parties: 'Copropriétaires et rôles', 'quote-parts': 'Quotes-parts', finances: 'Finances', approvals: 'Approbations', maintenance: 'Entretien',
      governance: 'Assemblées', reservations: 'Réservations', insurance: 'Assurances', automation: 'Automatisation', onboarding: 'Mise en service', settings: 'Paramètres',
    },
  },
  automation: {
    title: 'Automatisation',
    intro: 'Pour chaque processus, vous choisissez le degré d’autonomie des agents. Au départ, tout passe par votre confirmation; quand vous leur faites confiance, passez un processus en mode automatique. Tout ce qui est fait automatiquement est consigné ci-dessous.',
    boardOnly: 'L’automatisation est configurée par le conseil.',
    directorsOnly: 'Seul un administrateur peut changer le mode d’un processus.',
    levels: { confirm: 'Confirmation', auto: 'Automatique' },
    guardrails: 'Garde-fous',
    default: 'Réglage initial : confirmation par une personne',
    changed: (d: string, who: string) => `Modifié le ${d} par ${who}`,
    recent: (n: number) => (n === 0 ? 'aucune action automatique ces 30 derniers jours' : `${n} action${n > 1 ? 's' : ''} automatique${n > 1 ? 's' : ''} ces 30 derniers jours`),
    confirm: (process: string, level: string) => `Passer « ${process} » en mode ${level.toLowerCase()}? Note pour l’historique (facultatif) :`,
    noDecisions: 'Pas encore de décisions du conseil sur ce que l’agent a préparé.',
    accuracy: (agreed: number, total: number) => `Fiabilité (90 jours) : le conseil a confirmé ${agreed} proposition${agreed > 1 ? 's' : ''} sur ${total} telles quelles (${Math.round((agreed / total) * 100)} %).`,
    ready: 'Prêt pour le mode automatique',
    integrityTitle: 'Intégrité du journal',
    integrityIntro: 'Chaque événement et chaque action automatique est enchaîné au précédent par une empreinte (SHA-256) : modifier, retirer ou insérer une ligne après coup se voit. Chaque jour, l’empreinte la plus récente est aussi déposée hors de la base de données.',
    integrityCheck: 'Vérifier l’intégrité',
    integrityOk: (events: number, actions: number): string => `Journal intact : ${events} événement(s) et ${actions} action(s) automatique(s) vérifiés.`,
    integrityBroken: 'Le journal a été altéré.',
    integrityBrokenAt: (stream: string, seq: number): string => `${stream} : rupture à la position ${seq}.`,
    integrityStreams: { outbox: 'Événements', automation_log: 'Actions automatiques' },
    integrityAnchorOk: (day: string): string => `Concorde avec l’empreinte déposée le ${day}.`,
    integrityAnchorMismatch: (day: string): string => `Ne concorde pas avec l’empreinte déposée le ${day} : l’historique a été réécrit ou réinitialisé depuis.`,
    integrityNoAnchor: 'Aucune empreinte externe des 7 derniers jours (elle est déposée chaque jour).',
    limitLabel: 'Montant autorisé sans soumission',
    limitNone: 'à définir avant le mode automatique',
    limitSave: 'Enregistrer le montant',
    limitShort: (amount: string) => `jusqu’à ${amount}`,
    processes: {
      'reservations.approve': {
        title: 'Approbation des réservations',
        confirm: 'les réservations des installations qui le demandent attendent l’approbation du conseil.',
        auto: 'une réservation conforme aux règles est approuvée aussitôt et la personne reçoit sa confirmation.',
        guardrails: 'seulement si l’unité n’a aucun montant en retard de plus de 30 jours; les règles de l’installation (délais, quotas, fermetures, chevauchements) s’appliquent toujours; un refus reste une décision du conseil',
      },
      'maintenance.dispatch': {
        title: 'Envoi des travaux aux fournisseurs',
        confirm: 'l’agent propose un bon de travail et un fournisseur pour chaque bris signalé; le conseil le crée et envoie le lien.',
        auto: 'le bon de travail est créé et le fournisseur reçoit son lien aussitôt, avec le montant autorisé; au-delà, il doit transmettre une soumission.',
        guardrails: 'travaux non urgents seulement (une urgence reste humaine), fournisseur actif, joignable par courriel, assurance en vigueur et licence RBQ pour les métiers qui l’exigent; jamais si un bon existe déjà pour la demande; aucun paiement',
      },
      'maintenance.invoices': {
        title: 'Approbation des factures des fournisseurs',
        confirm: 'l’agent lit chaque facture et la vérifie contre le bon de travail; le conseil l’approuve ou la refuse.',
        auto: 'une facture conforme est approuvée aussitôt : la dépense et le montant dû au fournisseur sont inscrits aux livres.',
        guardrails: 'seulement une facture lue avec confiance, sans aucun écart (bon fournisseur, travail terminé, montant dans l’estimation, numéro non répété, TPS/TVQ exactes) et sous le montant fixé; inscrite au compte d’entretien du fonds d’administration; aucun paiement : le paiement reste une opération bancaire du conseil',
        limitLabel: 'Montant maximal d’une facture approuvée automatiquement',
      },
      'requests.reply': {
        title: 'Réponses aux demandes des copropriétaires',
        confirm: 'l’agent rédige une réponse tirée des documents; un membre du conseil la vérifie et l’envoie.',
        auto: 'la réponse est envoyée aussitôt quand elle est sûre, avec la mention « réponse automatique ».',
        guardrails: 'questions d’information seulement, réponse appuyée sur un article cité, confiance élevée; jamais ce qui demande une décision du conseil (autorisation, plainte, travaux, argent), jamais une urgence, jamais si quelqu’un a déjà répondu',
      },
      'collections.reminders': {
        title: 'Rappels de paiement',
        confirm: 'chaque lot de rappels attend l’approbation d’un administrateur.',
        auto: 'les rappels partent à 15 et 30 jours de retard, sans approbation.',
        guardrails: 'jamais tant que des dépôts bancaires restent à concilier; ceux qui ont payé entre-temps sont retirés; après deux rappels, le dossier va au conseil; aucune mise en demeure ni poursuite',
      },
      'ledger.bank_match': {
        title: 'Conciliation bancaire',
        confirm: 'Syndiqia propose une correspondance pour chaque transaction importée; le conseil confirme.',
        auto: 'les correspondances sûres sont inscrites dès l’import; les autres attendent le conseil.',
        guardrails: 'seulement les correspondances de confiance élevée (montant exact et référence reconnue); une erreur se corrige par une écriture de contrepassation',
      },
      'insurance.review': {
        title: 'Vérification des attestations d’assurance',
        confirm: 'le conseil accepte ou refuse chaque attestation reçue.',
        auto: 'une attestation conforme est acceptée dès sa réception.',
        guardrails: 'lue avec une confiance élevée et non modifiée par la personne, assureur et police présents, montant au moins égal au minimum exigé, en vigueur aujourd’hui; un refus reste toujours une décision du conseil',
      },
    },
    alwaysHumanTitle: 'Toujours une décision humaine',
    alwaysHumanIntro: 'Ces décisions ne peuvent pas être automatisées, quel que soit le réglage.',
    alwaysHuman: [
      'Sortie d’argent et paiement des fournisseurs (l’envoi automatique des travaux ne paie rien)',
      'Amendes et application des règlements',
      'Cotisations spéciales et stratégie du fonds de prévoyance',
      'Avis de convocation et autres avis légaux',
      'Mise en demeure, litiges et recours devant les tribunaux',
      'Urgences (jamais envoyées automatiquement à un fournisseur)',
    ],
    logTitle: 'Actions automatiques',
    logNone: 'Aucune action automatique pour le moment.',
    historyTitle: 'Historique des réglages',
  },
  board: {
    composition: 'Composition du conseil',
    titlesHeading: 'Fonctions au conseil',
    titles: { president: 'Président(e)', vice_president: 'Vice-président(e)', secretary: 'Secrétaire', treasurer: 'Trésorier(ère)', director: 'Administrateur(trice)' },
    until: (d: string) => `jusqu’au ${d}`,
    noEnd: 'sans date de fin',
    endingSoon: 'Mandat à renouveler',
    noTitle: 'Aucune fonction inscrite.',
    past: 'terminé',
    title: 'Fonction',
    startsOn: 'Début du mandat',
    endsOn: 'Fin du mandat',
    endsOnHint: 'Souvent la prochaine assemblée annuelle',
    note: 'Note',
    notePlaceholder: 'p. ex. élu à l’assemblée du 12 mai',
    appoint: 'Ajouter la fonction',
    endToday: 'Terminer aujourd’hui',
    delete: 'Supprimer',
    confirmEnd: (title: string, name: string) => `Terminer le mandat de ${title} de ${name} aujourd’hui?`,
    confirmDelete: 'Supprimer cette fonction (saisie erronée)? Pour un mandat terminé, utilisez plutôt « Terminer ».',
  },
  vendor: {
    title: (n: number): string => `Bon de travail no ${n}`,
    forSyndicate: (name: string): string => `Pour ${name}`,
    invalid: 'Ce lien n’est plus valide (révoqué, expiré, ou dossier clos). Communiquez avec le conseil.',
    location: 'Emplacement',
    scheduled: 'Date prévue',
    none: 'À convenir',
    accept: 'J’accepte le travail',
    decline: 'Je ne peux pas',
    proposeDate: 'Proposer une date',
    date: 'Date',
    addNote: 'Ajouter un message',
    note: 'Message',
    attach: 'Joindre un fichier (soumission, facture, photo)',
    fileHint: 'PDF, JPEG, PNG ou HEIC, 20 Mo max.',
    done: 'Travaux terminés',
    doneHint: 'Le conseil confirmera après vérification.',
    send: 'Envoyer',
    sent: 'Envoyé. Merci!',
    history: 'Historique',
    events: { accepted: 'Travail accepté', declined: 'Travail décliné', date_proposed: 'Date proposée', note: 'Message', file: 'Fichier joint', done: 'Travaux signalés terminés' },
    sources: { vendor: 'Fournisseur', board: 'Conseil' },
    access: 'Accès réservé au fournisseur mandaté. Ne transférez pas ce lien.',
    // Board side
    panel: 'Fournisseur',
    noVendor: 'Choisissez un fournisseur pour lui donner accès au bon de travail.',
    linkActive: (until: string): string => `Lien actif jusqu’au ${until}`,
    linkUsed: (when: string): string => `Ouvert pour la dernière fois le ${when}`,
    linkNever: 'Pas encore ouvert',
    noLink: 'Le fournisseur n’a pas encore de lien vers ce bon de travail.',
    sendLink: 'Envoyer le lien par courriel',
    sentTo: (email: string): string => `Lien envoyé à ${email}.`,
    copyLink: 'Créer un lien à copier',
    copied: 'Copié',
    revoke: 'Révoquer le lien',
    linkHint: 'Sans compte : le fournisseur peut accepter, proposer une date, joindre une soumission, une facture ou des photos, et signaler la fin des travaux. Un nouveau lien remplace l’ancien.',
    boardNote: 'Message au fournisseur',
  },
  purchasing: {
    // Board: quotes
    quotes: 'Soumissions',
    quotesHint: 'Invitez jusqu’à 5 fournisseurs : chacun reçoit son propre lien, sans compte, pour indiquer son prix (taxes en sus), son délai et joindre sa soumission. Retenir une soumission confie le travail à ce fournisseur et prévient les autres.',
    invite: 'Demander des soumissions',
    inviteSend: 'Envoyer les demandes',
    dueOn: 'Réponse souhaitée avant le',
    noEmail: 'sans courriel',
    invited: (n: number): string => `${n} demande(s) envoyée(s).`,
    vendor: 'Fournisseur',
    amount: 'Prix (avant taxes)',
    delay: 'Délai',
    days: (n: number): string => `${n} jour(s)`,
    validUntil: 'Valide jusqu’au',
    exclusions: 'Exclusions lues',
    file: 'Document',
    retain: 'Retenir',
    retained: (email: string | null): string => (email ? `Soumission retenue. Le bon de travail est confié au fournisseur et son lien envoyé à ${email}.` : 'Soumission retenue.'),
    quoteStatuses: { requested: 'En attente', received: 'Reçue', declined_by_vendor: 'Déclinée', selected: 'Retenue', not_selected: 'Non retenue' },
    lowest: 'la plus basse',
    // Board: invoices
    invoices: 'Factures',
    invoicesHint: 'L’agent lit chaque facture (PDF) et Syndiqia la vérifie contre le bon de travail. Approuver inscrit la dépense et le montant dû au fournisseur aux livres; le paiement se fait à la banque, puis se concilie.',
    addInvoice: 'Ajouter une facture reçue',
    addInvoiceHint: 'PDF lu automatiquement. Pour une photo ou un PDF numérisé, saisissez les montants.',
    number: 'No de facture',
    date: 'Date',
    subtotal: 'Sous-total',
    taxes: 'TPS + TVQ',
    total: 'Total',
    source: { vendor: 'par le lien du fournisseur', board: 'ajoutée par le conseil', email: 'reçue par courriel' },
    invoiceStatuses: { received: 'À approuver', approved: 'Approuvée', rejected: 'Refusée' },
    clean: 'Conforme au bon de travail',
    issues: {
      vendor_mismatch: 'Ce n’est pas le fournisseur du bon de travail',
      vendor_name_mismatch: 'Le nom sur la facture ne correspond pas au fournisseur',
      not_completed: 'Le bon de travail n’est pas terminé',
      no_estimate: 'Aucune estimation autorisée pour comparer',
      over_estimate: 'Dépasse l’estimation de plus de 10 %',
      duplicate_number: 'Numéro de facture déjà reçu de ce fournisseur',
      tax_arithmetic: 'TPS/TVQ ne correspondent pas au sous-total',
      total_arithmetic: 'Sous-total + taxes ≠ total',
      unread: 'Fichier non lu : montants saisis à vérifier',
      missing_total: 'Total illisible : à saisir avant d’approuver',
    },
    overBy: (amount: string): string => ` (de ${amount})`,
    expectedTaxes: (amount: string): string => ` (attendu : ${amount})`,
    account: 'Compte de dépense',
    fund: 'Fonds',
    approve: 'Approuver',
    reject: 'Refuser',
    rejectReason: 'Motif du refus (visible par le fournisseur)',
    edit: 'Corriger les montants',
    save: 'Enregistrer',
    view: 'Voir la facture',
    toApprove: (n: number): string => `${n} facture(s) de fournisseur à approuver`,
    toFile: (n: number): string => `${n} facture(s) reçue(s) par courriel à classer`,
    toFileTitle: (n: number): string => `Factures à classer (${n})`,
    toFileHint: 'Reçues à l’adresse de facturation sans bon de travail reconnu. Choisissez le bon de travail : la facture est alors vérifiée et attend votre approbation.',
    workOrder: 'Bon de travail',
    chooseWorkOrder: 'Choisir le bon de travail…',
    suggested: 'Suggestions',
    allWorkOrders: 'Tous les bons de travail',
    suggestionReasons: { vendor: 'même fournisseur', amount: 'montant proche', recent: 'récent' },
    fileIt: 'Classer',
    notInvoice: 'Pas une facture',
    totalUnknown: 'total à saisir',
    matchedBy: { number: 'no de bon de travail sur la facture', only_job: 'seul travail en cours de ce fournisseur' },
    invoiceAddressHint: (address: string): string => `Les fournisseurs envoient leurs factures à ${address} : elles sont lues et associées au bon de travail automatiquement.`,
    toCompare: (n: number): string => `${n} bon(s) de travail : soumissions reçues à comparer`,
    // Contractor page
    quoteTitle: 'Demande de soumission',
    quoteIntro: 'Indiquez votre prix avant taxes, votre délai et joignez votre soumission. Vous pouvez la modifier tant que le conseil n’a pas décidé.',
    quoteAmount: 'Prix avant taxes ($)',
    quoteDelay: 'Délai (jours)',
    quoteValid: 'Prix valide jusqu’au',
    quoteFile: 'Soumission (PDF ou photo)',
    quoteSend: 'Envoyer ma soumission',
    quoteDecline: 'Je ne soumissionne pas',
    quoteSent: 'Soumission reçue. Merci! Le conseil vous informera de sa décision.',
    yourQuote: 'Votre soumission',
    sendInvoice: 'Envoyer ma facture',
    invoiceHint: 'PDF de préférence. Le conseil la vérifie et l’approuve; le paiement suit selon les modalités convenues.',
    invoiceNumber: 'No de facture (facultatif si lisible dans le PDF)',
    invoiceTotal: 'Total taxes incluses ($) (facultatif si lisible)',
    yourInvoices: 'Vos factures',
    closedWork: 'Ce travail est terminé. Vous pouvez encore transmettre votre facture.',
    billing: 'Facturation :',
    invoiceByEmail: (address: string, n: number): string => `envoyez votre facture par courriel à ${address}, en indiquant « bon de travail no ${n} ». Vous pouvez aussi la téléverser ici.`,
    uploadInvoice: 'Téléverser une facture',
  },
  onboarding: {
    title: 'Mise en service',
    toReview: (n: number) => `Mise en service : ${n} étape(s) à vérifier et appliquer`,
    toStart: 'Mise en service : donnez-nous vos documents, votre liste de propriétaires et vos finances',
    intro: 'Donnez-nous ce que vous avez, sujet par sujet, dans le format que vous avez : PDF, Word, Excel, CSV. Syndiqia lit tout, vous dit ce qui est complet et ce qui manque, et vous montre ce qu’il a compris. Rien n’est enregistré avant que vous cliquiez sur « Appliquer ».',
    existing: (lots: number, parties: number, documents: number) => `Déjà dans Syndiqia : ${lots} lot(s), ${parties} personne(s), ${documents} document(s).`,
    statuses: { empty: 'À faire', reading: 'Lecture en cours…', ready: 'À vérifier', applied: 'Fait', skipped: 'Passée' },
    consoleIntro: (name: string) => `Mise en service de ${name}. Chaque phase est enregistrée dès qu’elle est faite : vous pouvez arrêter et reprendre plus tard depuis la console. La dernière phase invite le conseil d’administration et active le client.`,
    previous: 'Phase précédente',
    next: 'Phase suivante',
    saveQuit: 'Enregistrer et revenir à la console',
    quit: 'Revenir à l’accueil',
    skip: 'Passer cette phase pour l’instant',
    saveContinue: 'Enregistrer et continuer',
    identity: { address: 'Adresse principale', city: 'Ville', postal: 'Code postal', contact: 'Personne-ressource (présidence du conseil)', contactName: 'Nom', contactEmail: 'Courriel', contactPhone: 'Téléphone', lots: 'Nombre de lots (approximatif)', plan: 'Forfait', notes: 'Notes', none: '—', plans: { autonome: 'Autonome', assistee: 'Assistée', complete: 'Complète' } },
    board: { emailsNotSent: (emails: string) => `Client activé. Le courriel d’invitation n’a pas pu être envoyé à : ${emails}. Tant que l’envoi de courriels est en mode test chez Amazon (SES), seules les adresses vérifiées le reçoivent; réinvitez ces personnes depuis Membres après la vérification.`, intro: 'Choisissez les administrateurs parmi les personnes importées. Ils reçoivent leur invitation (création du mot de passe), le client devient actif et l’accès de l’équipe Syndiqia se ferme : ensuite, seulement par un accès de soutien approuvé par le conseil.', search: 'Rechercher une personne', noEmail: 'sans courriel', owner: 'propriétaire', save: 'Enregistrer le choix', activate: 'Inviter le conseil et activer le client', confirm: (n: number) => `Inviter ${n} administrateur(s) et activer le client? L’accès de l’équipe Syndiqia se fermera.`, done: 'Client activé : les administrateurs ont reçu leur invitation.', noPeople: 'Importez d’abord les lots et propriétaires (phase 3).' },
    steps: {
      identity: { title: 'Identification', lookFor: ['Adresse principale (elle crée l’immeuble ou le domaine)', 'Personne-ressource du conseil', 'Nombre de lots et forfait choisi'] },
      documents: { title: 'Documents de l’association', lookFor: ['Lettres patentes (ou déclaration de copropriété)', 'Règlements généraux et acte de servitudes', 'Procès-verbaux des dernières assemblées', 'Police d’assurance, autres avis'] },
      members: { title: 'Lots et propriétaires', lookFor: ['La liste des lots et de leurs propriétaires (Excel ou CSV idéalement)', 'Courriels, téléphones, adresses postales, langue', 'Quotes-parts ou parts, s’il y en a'] },
      finances: { title: 'Finances', lookFor: ['Les derniers états financiers', 'Le budget de l’année', 'Les soldes bancaires (fonctionnement, réserve)', 'Les montants dus par lot'] },
      vendors: { title: 'Fournisseurs et contrats', lookFor: ['Liste des fournisseurs (nom, courriel, téléphone)', 'Contrats en cours : déneigement, entretien, assurances…'] },
      board: { title: 'Conseil et activation', lookFor: ['Les administrateurs, choisis parmi les propriétaires importés', 'Un courriel pour chacun'] },
    },
    lookForTitle: 'Ce qu’on cherche',
    drop: 'Ajouter des fichiers',
    dropHint: 'Plusieurs fichiers à la fois · PDF, Word (.docx), Excel (.xlsx), CSV, texte · 20 Mo chacun',
    upload: 'Téléverser',
    uploading: 'Téléversement…',
    files: 'Fichiers reçus',
    readingHint: 'Syndiqia lit vos fichiers. Cela prend en général moins d’une minute.',
    checksTitle: 'Ce que Syndiqia a trouvé',
    apply: 'Appliquer',
    applyHint: 'Enregistre ce qui est proposé ci-dessous. Vous pourrez tout corriger ensuite dans l’application.',
    applyBlocked: 'Complétez ce qui manque (en rouge) avant d’appliquer.',
    reset: 'Recommencer cette étape',
    applied: 'Appliqué',
    applyRules: 'Appliquer aussi les règles d’assemblée trouvées',
    save: 'Enregistrer la correction',
    more: (n: number) => `et ${n} autre(s)`,
    preview: {
      document: 'Document', category: 'Catégorie', date: 'Date', visibility: 'Visible par', members: 'Membres', board: 'Conseil', unreadable: 'illisible (photo ou scan ?)',
      rules: 'Règles d’assemblée trouvées', voteBasis: { quote_parts: 'vote selon les quotes-parts', lots: 'une voix par lot', persons: 'une voix par membre' }, quorum: (n: number) => `quorum ${n} %`, notice: (n: number) => `avis d’au moins ${n} jours`, boardQuorum: (n: number) => `quorum du conseil ${n} %`,
      address: 'Adresse principale', name: 'Nom', street: 'Adresse', city: 'Ville', postal: 'Code postal',
      lot: 'Lot', owners: 'Propriétaires', email: 'Courriel', phone: 'Téléphone', share: 'Part',
      fiscalYear: 'Exercice', balancesDate: 'Soldes au', operating: 'Compte de fonctionnement', reserve: 'Compte du fonds de réserve', investments: 'Placements', arrears: 'Montants dus', budget: 'Budget', account: 'Compte', fund: 'Fonds', amount: 'Montant',
      vendor: 'Fournisseur', trade: 'Métier', service: 'Service',
    },
    categories: { declaration: 'Déclaration de copropriété', letters_patent: 'Lettres patentes', bylaws: 'Règlements', servitudes: 'Servitudes', minutes: 'Procès-verbal', insurance: 'Assurance', contingency_study: 'Étude du fonds', logbook: 'Carnet d’entretien', financial_statements: 'États financiers', notice: 'Avis', other: 'Autre' },
    checks: {
      founding_found: () => 'Document fondateur reçu',
      founding_missing: () => 'Il manque le document fondateur (lettres patentes ou déclaration de copropriété)',
      bylaws_found: () => 'Règlements reçus',
      bylaws_missing: () => 'Il manque les règlements',
      servitudes_found: () => 'Acte de servitudes reçu',
      servitudes_missing: () => 'Pas d’acte de servitudes : à ajouter s’il y en a un',
      minutes_found: (n: number) => `${n} procès-verbal(aux) reçu(s)`,
      minutes_missing: () => 'Aucun procès-verbal : ajoutez au moins celui de la dernière assemblée',
      insurance_found: () => 'Police d’assurance reçue',
      rules_found: () => 'Règles d’assemblée trouvées dans les règlements',
      rules_partial: () => 'Règles d’assemblée trouvées en partie : vérifiez-les',
      rules_missing: () => 'Règles d’assemblée introuvables : les valeurs par défaut s’appliqueront',
      unreadable: (n: number) => `${n} fichier(s) illisible(s) (photo ou scan sans texte) : envoyez un PDF avec texte si possible`,
      lots: (n: number) => `${n} lot(s) trouvé(s)`,
      lots_missing: () => 'Aucun lot trouvé dans ces fichiers',
      owners: (n: number) => `${n} propriétaire(s) trouvé(s)`,
      lots_without_owner: (n: number) => `${n} lot(s) sans propriétaire`,
      no_email: (n: number) => `${n} propriétaire(s) sans courriel`,
      all_emails: () => 'Tous les propriétaires ont un courriel',
      no_phone: (n: number) => `${n} propriétaire(s) sans téléphone`,
      all_phones: () => 'Tous les propriétaires ont un téléphone',
      duplicate_lots: (n: number) => `${n} lot(s) en double`,
      shares_ok: () => 'Parts trouvées pour chaque lot (total 100 %)',
      shares_sum: (n: number) => `Les parts totalisent ${n} % au lieu de 100 % : vérifiez`,
      shares_partial: (n: number) => `${n} lot(s) sans part`,
      equal_shares: () => 'Pas de parts dans la liste : parts égales par lot',
      shares_missing: () => 'Pas de quotes-parts dans la liste : à saisir d’après la déclaration',
      address_missing: () => 'Il manque l’adresse principale : complétez-la ci-dessous',
      truncated: () => 'Le document contient plus de lots que ce qui a pu être lu : envoyez la liste en Excel ou CSV',
      fiscal_year: () => 'Exercice financier trouvé',
      fiscal_year_missing: () => 'Exercice financier introuvable : précisez-le',
      bank_operating: () => 'Solde du compte de fonctionnement trouvé',
      bank_operating_missing: () => 'Il manque le solde du compte de fonctionnement',
      bank_reserve: () => 'Solde du fonds de réserve trouvé',
      bank_reserve_missing: () => 'Pas de solde du fonds de réserve',
      budget: (n: number) => `Budget trouvé (dépenses : ${n.toLocaleString('fr-CA')} $)`,
      budget_missing: () => 'Pas de budget de l’année',
      arrears: (n: number) => `${n} montant(s) dû(s) par des lots`,
      arrears_unknown_lots: (n: number) => `${n} montant(s) dû(s) sur des lots inconnus`,
      arrears_none: () => 'Aucun montant dû trouvé : est-ce exact ?',
      balances_date_missing: () => 'Date des soldes introuvable : début de l’exercice utilisé',
      vendors: (n: number) => `${n} fournisseur(s) trouvé(s)`,
      vendors_missing: () => 'Aucun fournisseur trouvé',
      vendors_no_email: (n: number) => `${n} fournisseur(s) sans courriel`,
      address_given: () => 'Adresse principale indiquée',
      contact_given: () => 'Personne-ressource indiquée',
      contact_missing: () => 'Pas de courriel de personne-ressource',
      plan_chosen: () => 'Forfait choisi',
      plan_missing: () => 'Forfait à choisir',
      directors: (n: number) => `${n} administrateur(s) choisi(s)`,
      directors_missing: () => 'Choisissez au moins un administrateur',
      directors_no_email: (n: number) => `${n} administrateur(s) sans courriel : complétez leur fiche`,
    } as Record<string, (n: number) => string>,
  },
  insurance: {
    title: 'Assurances des copropriétaires',
    intro: 'Chaque copropriétaire doit détenir une assurance responsabilité civile et en fournir la preuve au conseil. Les attestations sont des renseignements personnels : seul le conseil les voit.',
    statuses: { valid: 'Valide', expiring: 'Expire bientôt', expired: 'Expirée', missing: 'Manquante', insufficient: 'Montant insuffisant', submitted: 'À vérifier' },
    certStatuses: { submitted: 'En vérification', accepted: 'Acceptée', rejected: 'Refusée' },
    roles: { owner: 'Copropriétaire', occupant: 'Occupant' },
    summary: (missing: number, expired: number, expiring: number): string => `${missing} manquante(s) · ${expired} expirée(s) · ${expiring} à renouveler`,
    toReview: 'À vérifier',
    noneToReview: 'Aucune attestation à vérifier.',
    coverage: 'Par unité',
    unit: 'Unité',
    person: 'Personne',
    insurer: 'Assureur',
    policy: 'No de police',
    liability: 'Responsabilité civile',
    startsOn: 'Début',
    expiresOn: 'Expiration',
    file: 'Voir l’attestation',
    accept: 'Accepter',
    reject: 'Refuser',
    reason: 'Motif du refus (envoyé à la personne)',
    uploadFor: 'Téléverser pour cette personne',
    requestMissing: 'Demander les attestations manquantes',
    requestHint: 'Un courriel à chaque copropriétaire sans attestation (au plus une fois par mois chacun).',
    requested: (sent: number, unreachable: number): string => `${sent} courriel(s) envoyé(s)${unreachable ? `, ${unreachable} sans courriel` : ''}.`,
    settings: 'Exigences',
    required: 'Responsabilité civile minimale',
    occupantsRequired: 'Les occupants doivent aussi fournir une attestation',
    readByAgent: 'Lu automatiquement dans le document : vérifiez et corrigez au besoin.',
    // Member
    mineTitle: 'Mon assurance',
    mineIntro: (amount: string): string => `Téléversez votre attestation d’assurance (PDF ou photo). Responsabilité civile exigée : au moins ${amount}.`,
    upload: 'Téléverser mon attestation',
    uploading: 'Lecture du document…',
    noneYet: 'Aucune attestation au dossier.',
    rejectedBecause: (reason: string): string => `Refusée : ${reason}`,
    todoReview: (n: number): string => (n === 1 ? '1 attestation d’assurance à vérifier' : `${n} attestations d’assurance à vérifier`),
    todoMissing: (n: number): string => (n === 1 ? '1 copropriétaire sans assurance valide' : `${n} copropriétaires sans assurance valide`),
  },
  nav: { home: 'Accueil', requests: 'Demandes', documents: 'Documents', units: 'Unités', parties: 'Copropriétaires', quoteParts: 'Quotes-parts', finances: 'Finances', maintenance: 'Entretien', governance: 'Assemblées', reservations: 'Réservations', insurance: 'Assurances', automation: 'Automatisation', onboarding: 'Mise en service', settings: 'Paramètres', profile: 'Mon profil' },
  common: {
    save: 'Enregistrer',
    cancel: 'Annuler',
    add: 'Ajouter',
    optional: 'facultatif',
    none: 'Aucun',
    back: 'Retour',
    error: 'Une erreur est survenue.',
    errorWithReference: (ref: string) => `Une erreur inattendue est survenue (référence ${ref}). Réessayez; si elle revient, transmettez cette référence au soutien Syndiqia.`,
    forbidden: "Vous n'avez pas les droits nécessaires pour cette action.",
    notFound: 'Introuvable.',
    conflict: 'Cette modification entre en conflit avec les données existantes.',
    invalid: 'Certains champs sont invalides.',
  },
  home: {
    todo: 'À faire',
    allDone: 'Rien en attente. 👍',
    reconcile: (n: number) => (n === 1 ? '1 transaction bancaire à concilier' : `${n} transactions bancaires à concilier`),
    callsDue: (n: number) => (n === 1 ? '1 appel de contributions à émettre' : `${n} appels de contributions à émettre`),
    transferDue: (amount: string) => `Virement de ${amount} à faire vers les autres fonds`,
    budgetDraft: "Le budget de l'exercice n'est pas encore adopté",
    noBudget: "Aucun budget pour l'exercice en cours",
    noYear: 'Les finances ne sont pas encore configurées',
    noQuoteParts: 'Aucune répartition des quotes-parts en vigueur',
    unitsWithoutOwner: (n: number) => (n === 1 ? "1 unité sans propriétaire inscrit" : `${n} unités sans propriétaire inscrit`),
    arrears: 'Comptes en souffrance',
    overdueTotal: (amount: string, n: number) => `${amount} en retard, ${n === 1 ? '1 unité' : `${n} unités`}`,
    seeAll: 'Voir tout',
    funds: 'Fonds',
    syndicate: 'Le syndicat',
    buildings: (n: number) => (n === 1 ? '1 immeuble' : `${n} immeubles`),
    units: (n: number) => (n === 1 ? '1 unité' : `${n} unités`),
    coOwners: (n: number) => (n === 1 ? '1 copropriétaire' : `${n} copropriétaires`),
    quotePartsSince: (d: string) => `Quotes-parts en vigueur depuis le ${d}`,
    ownerSpace: 'Votre espace copropriétaire (votre compte, les documents, vos demandes) arrive bientôt.',
  },
  unitsPage: { title: 'Unités', quotePart: 'Quote-part', owners: 'Propriétaires', balance: 'Solde', empty: 'Aucune unité. Ajoutez-les dans Paramètres.' },
  settings: {
    title: 'Paramètres du syndicat',
    intro: "Configuration faite une fois, à la mise en service : immeubles et unités. Le reste du travail courant se fait dans les autres sections.",
    buildings: 'Immeubles',
    addUnits: 'Ajouter des unités',
    structure: 'Type de regroupement',
    structures: {
      divided_coownership: 'Syndicat de copropriétaires (copropriété divise)',
      npo_with_servitudes: 'Association de propriétaires (organisme sans but lucratif, servitudes)',
    },
    structureHint: 'Fixé à l’ouverture du compte. Il détermine les règles qui s’appliquent (Code civil et Loi 16, ou règlements de l’association).',
    rulesTitle: 'Règles des assemblées',
    boardRules: 'Réunions du conseil',
    boardQuorum: 'Quorum du conseil (%)',
    boardQuorumHint: '50 = la majorité des administrateurs',
    boardNotice: 'Avis aux administrateurs (jours avant)',
    boardCasting: 'Le président de la réunion a une voix prépondérante en cas d’égalité',
    rulesIntro: 'Selon la déclaration de copropriété ou les règlements de l’association. Elles servent à la feuille de présence, au quorum et à l’avis de convocation.',
    rulesDefault: 'Valeurs par défaut pour ce type de regroupement : à confirmer selon vos documents.',
    voteBasis: 'Calcul des voix',
    voteBases: { quote_parts: 'Selon les quotes-parts', lots: 'Une voix par lot', persons: 'Une voix par membre' },
    quorumPercent: 'Quorum (% des voix)',
    quorumHint: '50 % = majorité des voix (plus de la moitié); autre valeur = au moins ce pourcentage.',
    noticeMin: 'Avis : au moins (jours avant)',
    noticeMax: 'Avis : au plus (jours avant)',
    rulesSaved: 'Règles enregistrées.',
    accessTitle: 'Revue des accès',
    accessIntro: 'Qui a accès à ce syndicat, avec quels rôles. À revoir régulièrement (Loi 25) : retirez les accès qui ne servent plus.',
    lastSeen: (d: string): string => `Vu le ${d}`,
    neverSeen: 'Jamais connecté',
    flags: { never_used: 'Invitation jamais utilisée', no_record: 'Aucune fiche liée' },
    removeAccess: 'Retirer l’accès',
    confirmRemove: (name: string): string => `Retirer l’accès de ${name} à ce syndicat? Son compte reste actif ailleurs.`,
    boardRolesHint: 'Les rôles du conseil se retirent depuis la fiche de la personne.',
    vendorLinks: 'Liens actifs des fournisseurs',
    noVendorLinks: 'Aucun lien actif.',
    vendorLink: (n: number, title: string): string => `Bon no ${n} — ${title}`,
  },
  requests: {
    attachments: 'Pièces jointes',
    attach: 'Joindre des photos ou des PDF',
    removeFile: 'retirer',
    title: 'Demandes',
    byEmail: (address: string) => `Vous pouvez aussi écrire à ${address} : votre courriel devient une demande, et vous pouvez répondre aux messages du conseil par courriel.`,
    byEmailBoard: (address: string) => `Les courriels envoyés à ${address} deviennent des demandes; les réponses par courriel s’ajoutent à la bonne demande. Donnez cette adresse aux copropriétaires.`,
    new: 'Nouvelle demande',
    empty: 'Aucune demande.',
    filters: { open: 'Ouvertes', all: 'Toutes' },
    number: 'No',
    subject: 'Objet',
    category: 'Type',
    categories: {
      maintenance: 'Bris ou entretien',
      question: 'Question',
      renovation: 'Autorisation de travaux',
      document: 'Demande de document',
      complaint: 'Plainte ou nuisance',
      other: 'Autre',
    },
    priority: 'Priorité',
    urgent: 'Urgent',
    status: 'Statut',
    statuses: { new: 'Nouvelle', in_progress: 'En cours', waiting: 'En attente de votre réponse', resolved: 'Résolue' },
    statusesBoard: { new: 'Nouvelle', in_progress: 'En cours', waiting: 'En attente du demandeur', resolved: 'Résolue' },
    unit: 'Unité',
    noUnit: 'Parties communes / général',
    requester: 'Demandeur',
    onBehalfOf: 'Au nom de (demande reçue par téléphone ou courriel)',
    myself: 'Moi-même',
    description: 'Description',
    descriptionHint: 'Décrivez la situation : où, depuis quand, photos à venir…',
    updated: 'Mise à jour',
    assignee: 'Responsable',
    unassigned: 'Non assignée',
    reply: 'Répondre',
    replyPlaceholder: 'Votre message…',
    internal: 'Note interne (invisible pour le demandeur, jamais envoyée)',
    internalBadge: 'Note interne',
    send: 'Envoyer',
    emailed: (to: string) => `Courriel à ${to}`,
    deliveryStatus: { queued: 'en attente', sending: 'en cours', sent: 'envoyé', failed: 'échec' },
    back: 'Toutes les demandes',
    newCount: (n: number) => (n === 1 ? '1 nouvelle demande' : `${n} nouvelles demandes`),
    mine: 'Mes demandes',
  },
  emails: {
    title: 'Courriels envoyés',
    intro: 'Chaque courriel et son statut de livraison (preuve de transmission).',
    capture: 'Mode démonstration : les courriels sont enregistrés ici mais ne quittent pas le serveur.',
    to: 'Destinataire',
    empty: 'Aucun courriel.',
  },
  access: {
    title: 'Accès',
    none: 'Aucun accès',
    invited: (d: string) => `Invité le ${d}`,
    active: (d: string) => `Actif — vu le ${d}`,
    invite: 'Inviter',
    reinvite: 'Renvoyer l’invitation',
    noEmail: 'Courriel requis pour inviter',
    sent: 'Invitation envoyée.',
    emailNotSent: 'Accès accordé, mais le courriel d’invitation n’a pas pu être envoyé (envoi de courriels en mode test : seules les adresses vérifiées le reçoivent). Réinvitez après la vérification de l’adresse.',
    confirm: (name: string) => `Inviter ${name} à créer son accès en ligne? Un courriel lui sera envoyé pour choisir son mot de passe.`,
    myAccount: 'Mon compte',
    myUnits: 'Mes unités',
    noUnits: 'Aucune unité associée à votre compte.',
  },
  approvals: {
    title: 'Approbations',
    pending: 'À approuver',
    none: 'Aucune approbation en attente.',
    requestedBy: 'Préparé par',
    system: 'Syndiqia (automatique)',
    on: 'le',
    approve: (n: number) => (n === 1 ? 'Approuver l’envoi (1 courriel)' : `Approuver l’envoi (${n} courriels)`),
    approveOnly: 'Approuver',
    reject: 'Refuser',
    rejectReason: 'Motif du refus (facultatif)',
    include: 'Inclure',
    preview: 'Aperçu',
    emails: 'Courriels',
    escalations: 'Dossiers à examiner par le conseil',
    escalationHelp: 'Deux rappels sont restés sans effet. Le conseil décide de la suite (mise en demeure, entente de paiement…). Aucun courriel n’est envoyé.',
    unreachable: 'Sans adresse courriel — à joindre autrement',
    unreconciled: (n: number) => `Attention : ${n} dépôt(s) bancaire(s) non concilié(s). Certains copropriétaires ont peut-être déjà payé — conciliez la banque avant d’approuver.`,
    reconcile: 'Concilier maintenant',
    status: { pending: 'En attente', approved: 'Approuvé', rejected: 'Refusé', cancelled: 'Annulé' },
    result: (sent: number, skipped: number) => `${sent} courriel(s) envoyé(s)${skipped ? `, ${skipped} retiré(s) car payé(s) entre-temps` : ''}.`,
    stage: { 1: 'Premier rappel', 2: 'Deuxième rappel' } as Record<number, string>,
    canOnlyView: 'Seul un membre du conseil peut approuver cet envoi.',
    todo: (n: number) => (n === 1 ? 'Lot de rappels à approuver (1 courriel)' : `Lot de rappels à approuver (${n} courriels)`),
    prepare: 'Préparer les rappels de paiement',
    nothing: 'Aucun rappel à envoyer pour le moment.',
  },
  collections: {
    stage: { 1: 'Premier rappel envoyé', 2: 'Deuxième rappel envoyé', 3: 'Dossier transmis au conseil' } as Record<number, string>,
    escalated: (label: string) => `Recouvrement à examiner : unité ${label}`,
  },
  documents: {
    title: 'Documents',
    empty: 'Aucun document.',
    upload: 'Ajouter un document',
    file: 'Fichier (PDF ou texte, 20 Mo max.)',
    docTitle: 'Titre',
    category: 'Catégorie',
    categories: {
      declaration: 'Déclaration de copropriété',
      letters_patent: 'Lettres patentes',
      bylaws: 'Règlements',
      servitudes: 'Actes de servitude',
      minutes: 'Procès-verbaux',
      insurance: 'Assurances',
      contingency_study: 'Fonds de prévoyance',
      logbook: "Carnet d'entretien",
      financial_statements: 'États financiers',
      notice: 'Avis et communications',
      other: 'Autres',
    },
    visibility: 'Visible par',
    visibilities: { members: 'Tous les copropriétaires et occupants', board: 'Le conseil seulement' },
    boardOnly: 'Conseil seulement',
    date: 'Date du document',
    pages: (n: number) => (n === 1 ? '1 page' : `${n} pages`),
    open: 'Ouvrir',
    delete: 'Supprimer',
    confirmDelete: (title: string) => `Supprimer « ${title} »? Cette action est définitive.`,
    search: 'Rechercher dans les documents…',
    results: 'Résultats',
    noResults: 'Aucun passage trouvé.',
    page: (n: number) => `page ${n}`,
  },
  suggestion: {
    title: 'Suggestion de Syndiqia',
    subtitle: 'Brouillon préparé automatiquement à partir des documents du syndicat. Vérifiez-le avant de l’envoyer.',
    suggested: 'Type suggéré',
    apply: 'Appliquer',
    urgent: 'Urgence suggérée',
    draft: 'Brouillon de réponse',
    sources: 'Sources',
    boardDecision: 'Décision du conseil requise',
    internalNote: 'Note pour le conseil',
    use: 'Utiliser ce brouillon',
    dismiss: 'Ignorer',
    used: 'Brouillon utilisé.',
    dismissed: 'Suggestion ignorée.',
    confidence: { high: 'confiance élevée', medium: 'confiance moyenne', low: 'confiance faible' },
    page: (n: number) => `p. ${n}`,
  },
  ask: {
    title: 'Une question sur les règlements?',
    placeholder: 'Ex. : Puis-je avoir un chien? Quelles sont les heures pour faire des travaux?',
    ask: 'Poser la question',
    thinking: 'Recherche dans les documents du syndicat…',
    automatic: 'Réponse automatique tirée des documents du syndicat. Elle ne remplace pas une décision du conseil.',
    notCovered: 'Les documents ne semblent pas couvrir cette question.',
    forward: 'Transmettre ma question au conseil',
    forwarded: 'Question transmise au conseil : vous pouvez suivre la demande dans « Demandes ».',
    sources: 'Sources',
    previous: 'Mes questions récentes',
    shortcut: 'Poser une question sur les règlements',
  },
  buildings: {
    title: 'Immeubles',
    empty: 'Aucun immeuble pour le moment.',
    add: 'Ajouter un immeuble',
    name: 'Nom',
    streetAddress: 'Adresse',
    city: 'Ville',
    postalCode: 'Code postal',
    units: 'Unités',
  },
  units: {
    title: 'Unités',
    empty: 'Aucune unité dans cet immeuble.',
    add: 'Ajouter une unité',
    label: 'Numéro',
    kind: 'Type',
    cadastralLot: 'Lot cadastral',
    floor: 'Étage',
    area: 'Superficie (m²)',
    kinds: {
      residential: 'Résidentielle',
      commercial: 'Commerciale',
      parking: 'Stationnement',
      storage: 'Rangement',
      other: 'Autre',
    },
  },
  ownership: {
    title: 'Propriétaires',
    current: 'Propriétaires actuels',
    none: 'Aucun propriétaire inscrit.',
    record: 'Inscrire un changement de propriété',
    effectiveFrom: 'À compter du',
    owner: 'Propriétaire',
    share: 'Part indivise',
    addOwner: 'Ajouter un copropriétaire indivis',
    sharesMustSumToOne: 'Les parts doivent totaliser exactement 1.',
    since: 'depuis le',
    noParties: "Ajoutez d'abord les personnes dans « Copropriétaires ».",
  },
  parties: {
    title: 'Copropriétaires et contacts',
    empty: 'Aucune personne inscrite.',
    add: 'Ajouter une personne ou une organisation',
    kind: 'Type',
    kinds: { person: 'Personne', organization: 'Organisation' },
    displayName: 'Nom',
    email: 'Courriel',
    phone: 'Téléphone',
    preferredLanguage: 'Langue de communication',
    languages: { fr: 'Français', en: 'Anglais' },
    boardOnly: 'Les coordonnées sont réservées au conseil (Loi 25).',
  },
  finances: {
    title: 'Finances',
    overview: 'Sommaire',
    budget: 'Budget',
    journal: 'Journal',
    bank: 'Banque',
    noYear: "Aucun exercice financier. Créez le premier pour configurer le plan comptable et le budget.",
    createYear: "Créer l'exercice financier",
    yearLabel: 'Libellé',
    yearStart: 'Début',
    yearEnd: 'Fin (exclue)',
    year: 'Exercice',
    funds: 'Fonds',
    cash: 'Encaisse et placements',
    receivable: 'À recevoir des copropriétaires',
    netAssets: 'Actif net',
    arrears: 'Comptes en souffrance',
    noArrears: 'Aucun compte en souffrance.',
    unit: 'Unité',
    owners: 'Propriétaires',
    balance: 'Solde',
    current: 'Courant',
    d30: '1–30 j',
    d60: '31–60 j',
    d90: '61–90 j',
    over90: '90 j et +',
    creditBalance: 'Solde créditeur',
    callsDue: (n: number) => (n === 1 ? '1 appel de contributions à émettre' : `${n} appels de contributions à émettre`),
    postCalls: 'Émettre les appels échus',
    callsPosted: (n: number) => (n === 1 ? '1 appel émis.' : `${n} appels émis.`),
  },
  budget: {
    status: { draft: 'Brouillon', adopted: 'Adopté' },
    noBudget: "Aucun budget pour cet exercice.",
    contributions: 'Contributions des copropriétaires',
    otherRevenue: 'Autres revenus',
    expenses: 'Dépenses',
    total: 'Total',
    perMonth: 'par mois',
    installments: 'Versements par année',
    edit: 'Modifier le budget',
    save: 'Enregistrer le brouillon',
    adopt: 'Adopter le budget',
    adoptConfirm: "Une fois adopté, le budget ne peut plus être modifié et les appels de contributions sont planifiés. Continuer ?",
    calls: 'Appels de contributions',
    due: 'Échéance',
    posted: 'Émis',
    scheduled: 'Planifié',
    account: 'Compte',
    fund: 'Fonds',
    amount: 'Montant annuel',
    onlyNonZero: 'Afficher seulement les montants saisis',
  },
  journal: {
    title: 'Journal général',
    date: 'Date',
    description: 'Description',
    kind: {
      opening: "Ouverture",
      contribution_call: 'Appel de contributions',
      payment: 'Paiement',
      expense: 'Dépense',
      manual: 'Écriture',
      reversal: 'Annulation',
    },
    debit: 'Débit',
    credit: 'Crédit',
    reverse: 'Annuler par une écriture inverse',
    reversed: 'Annulée',
    reason: "Motif de l'annulation",
    showLines: 'Détail',
  },
  statement: {
    title: 'Compte du copropriétaire',
    empty: 'Aucune transaction.',
    charge: 'Facturé',
    payment: 'Payé',
    balance: 'Solde',
    byFund: 'Solde par fonds',
    recordPayment: 'Enregistrer un paiement',
    amount: 'Montant',
    date: 'Date',
    reference: 'Référence',
    noLedger: "La comptabilité n'est pas encore configurée.",
  },
  bank: {
    title: 'Banque',
    import: 'Importer un relevé bancaire',
    account: 'Compte bancaire',
    file: 'Fichier CSV exporté de la banque',
    mapping: 'Colonnes du fichier',
    skipRows: "Lignes d'en-tête à ignorer",
    dateColumn: 'Date',
    dateFormat: 'Format de date',
    descriptionColumn: 'Description',
    amountMode: 'Montants',
    oneColumn: 'Une colonne (dépôts positifs)',
    twoColumns: 'Deux colonnes (retraits / dépôts)',
    amountColumn: 'Montant',
    withdrawalColumn: 'Retraits',
    depositColumn: 'Dépôts',
    decimalComma: 'Virgule décimale (1 234,56)',
    preview: 'Aperçu',
    unreadable: (n: number) => `${n} ligne(s) illisible(s) — vérifiez les colonnes.`,
    doImport: 'Importer',
    imported: (n: number, d: number) => `${n} transaction(s) importée(s)${d ? `, ${d} déjà importée(s)` : ''}.`,
    tabs: { unmatched: 'À concilier', matched: 'Conciliées', ignored: 'Mises de côté' },
    none: 'Aucune transaction.',
    confirmAll: (n: number) => `Confirmer les ${n} suggestion(s) fiable(s)`,
    confirmed: (n: number) => `${n} transaction(s) conciliée(s).`,
    confirm: 'Confirmer',
    other: 'Autre…',
    suggestion: 'Suggestion',
    paymentFor: (units: string) => `Paiement — ${units}`,
    entryLink: (d: string) => `Écriture existante : ${d}`,
    confidence: { high: 'fiable', medium: 'à vérifier' },
    payUnits: 'Paiement de copropriétaire',
    units: 'Unité(s)',
    record: 'Enregistrer le paiement',
    postTo: 'Comptabiliser dans le compte',
    post: 'Comptabiliser',
    ignore: 'Mettre de côté',
    ignoreReason: 'Pourquoi mettre cette transaction de côté ?',
    deposit: 'Dépôt',
    withdrawal: 'Retrait',
    dueToFunds: (amounts: string) => `Le compte d'opérations détient ${amounts} pour les autres fonds.`,
    transfer: 'Enregistrer le virement',
  },
  quoteParts: {
    title: 'Quotes-parts (valeurs relatives)',
    intro:
      'Les valeurs relatives de la déclaration de copropriété. Elles servent à répartir les charges et à pondérer les votes.',
    current: 'En vigueur',
    none: 'Aucune répartition en vigueur.',
    unit: 'Unité',
    value: 'Valeur',
    percent: '%',
    total: 'Total',
    source: 'Source',
    from: 'Du',
    to: 'au',
    history: 'Historique',
    status: { active: 'En vigueur', draft: 'Brouillon' },
    new: 'Nouvelle répartition',
    denominator: 'Dénominateur',
    effectiveFrom: 'En vigueur à compter du',
    sourcePlaceholder: 'Déclaration de copropriété, annexe…',
    remaining: 'Reste à répartir',
    activate: 'Enregistrer et mettre en vigueur',
    needUnits: "Ajoutez d'abord des unités aux immeubles.",
    sumMismatch: 'La somme des valeurs doit égaler le dénominateur.',
  },
};

export type Messages = typeof fr;

export const en: Messages = {
  appName: 'Syndiqia',
  loading: 'Loading…',
  signIn: 'Sign in',
  signOut: 'Sign out',
  signingIn: 'Signing in…',
  signInError: 'Sign-in failed.',
  retry: 'Try again',
  language: 'Français',
  languageShort: 'FR',
  about: 'About Syndiqia',
  aboutShort: 'About',
  salesTools: {
    title: 'Sales tools',
    intro: 'Everything needed to sell Syndiqia: the demos, the call tools, the decks and the pages to show clients. Syndiqia team only.',
    internal: 'Internal',
    open: 'Open →',
    download: (format: string) => `Download (${format})`,
    downloadFailed: 'Download failed: try again',
  },
  console: {
    title: 'Syndiqia console',
    onboardingTag: 'Onboarding',
    onboardAssociation: 'Onboard an association',
    onboardSyndicate: 'Onboard a syndicate',
    newClientName: 'Client name',
    newClientNameHint: 'As in its letters patent or declaration',
    newClientSlug: 'Address in Syndiqia',
    newClientSlugHint: 'Lowercase letters, digits and dashes; also used for emails (demandes@…)',
    startOnboarding: 'Start onboarding',
    onboardingStatus: (done: number, total: number) => `Onboarding · ${done} / ${total}`,
    continueOnboarding: 'Continue',
    activeStatus: 'Active',
    intro: (roles: string) => `Syndiqia team space — your roles: ${roles}. You reach a real client's data only during its onboarding, or afterwards when one of its directors approved it.`,
    roles: { platform_admin: 'Platform admin', sales: 'Sales', support: 'Support' },
    demos: 'Demos',
    demosHint: 'Fictional data. You act as a director there; no email reaches real people.',
    demoBadge: 'Demo',
    own: 'Your communities',
    supportOpen: 'Support accesses in progress',
    supportBadge: 'Read-only',
    expires: (when: string) => `Expires on ${when}`,
    clients: 'Clients',
    clientsHint: 'Basic information only: no personal data is visible here.',
    noClients: 'No clients yet.',
    client: 'Client',
    kind: 'Type',
    size: 'Size',
    since: 'Since',
    units: (n: number) => (n === 1 ? '1 unit' : `${n} units`),
    askAccess: 'Request support access',
    reason: 'Reason',
    reasonHint: 'Seen by the client’s directors',
    hours: 'Duration',
    hoursOption: (h: number) => (h < 24 ? `${h} hours` : h === 24 ? '24 hours' : h === 168 ? '7 days' : `${h / 24} days`),
    send: 'Send the request',
    myRequests: 'Your access requests',
    statuses: { requested: 'Pending', approved: 'Approved', rejected: 'Refused', revoked: 'Withdrawn', expired: 'Expired' },
    team: 'Syndiqia team',
    addMember: 'Add a member',
    person: 'Person',
    rolesHeading: 'Roles',
    edit: 'Edit',
    roleHelp: {
      platform_admin: 'Manages the team and clients; opens demos.',
      sales: 'Opens and presents demos.',
      support: 'Requests temporary, read-only access to a client’s data, with the client’s approval.',
    },
    leaveWarning: 'With no role, the person leaves the Syndiqia team and loses access to the console.',
    confirmLeave: (who: string) => `Remove ${who} from the Syndiqia team?`,
    demoTag: 'Demo',
    viewAs: 'View as',
    personas: { director: 'Director (you)', manager: 'Manager', owner: 'Co-owner', occupant: 'Occupant' },
    reset: 'Reset the demo',
    resetting: 'Reset in progress: the demo will be ready in about a minute.',
    confirmReset: (name: string) => `Reset "${name}"? Every change made in this demo will be erased.`,
    email: 'Sign-in email',
    emailHint: 'If the person has no account yet, they receive an email to choose a password',
    invitationNotSent: (email: string) =>
      `The account of ${email} is created and the role added, but the email to choose a password could not be sent. While Syndiqia's email sending is in test mode at Amazon (SES), only verified addresses receive it: have the address verified, then send the invitation again.`,
    resendInvitation: 'Send the invitation again',
    invitationPending: 'Invitation pending',
    memberName: 'Name',
    memberNameHint: 'For a new person: the name shown in Syndiqia',
    invitationSent: (email: string) => `Invitation sent to ${email}: they choose a password from the email they received.`,
    role: 'Role',
    grant: 'Add the role',
    remove: 'Remove',
    confirmRemove: (role: string, who: string) => `Remove the "${role}" role from ${who}?`,
    bannerDemo: 'Demo mode: fictional data, you act as a director.',
    bannerSupport: (until: string) => `Read-only Syndiqia support access, approved by the board${until ? ` until ${until}` : ''}. Every page viewed is logged.`,
    back: 'Back to the console',
    panelTitle: 'Syndiqia support access',
    panelIntro: 'Syndiqia support sees your data only when a director approves it: temporarily, read-only, and every page viewed is logged. You can withdraw the access at any time.',
    panelNone: 'No requests.',
    panelAsked: (d: string, h: number) => `Requested on ${d}, for ${h} h`,
    panelViews: (n: number, last: string) => `${n} page${n > 1 ? 's' : ''} viewed${last ? `, last on ${last}` : ''}`,
    approve: 'Approve',
    reject: 'Refuse',
    revoke: 'Withdraw access',
    confirmApprove: (h: number) => `Give Syndiqia support read-only access for ${h} hours?`,
  },
  manual: {
    title: 'User manual',
    back: 'Back to the app',
    print: 'Print',
    contents: 'Contents',
    introduction: 'Introduction',
    roles: 'Roles',
    role: 'Role',
    canDo: 'What the person can do',
    glossary: 'Glossary',
    everyone: 'Everyone',
    board: 'Board',
    version: 'Guide version',
  },
  noSyndicate: 'Your account is not linked to any syndicate. Please contact your board.',
  chooseSyndicate: 'Choose a community',
  chooseIntro: 'Your account gives access to several syndicates or associations.',
  chooseOpen: 'Open',
  chooseKinds: { divided_coownership: 'Syndicate of co-owners', npo_with_servitudes: "Owners' association" },
  roleLabel: (n: number) => (n > 1 ? 'Roles: ' : 'Role: '),
  roles: { director: 'Board member', manager: 'Manager', owner: 'Co-owner', occupant: 'Occupant', vendor: 'Vendor', support: 'Syndiqia support (read-only)' },
  maintenance: {
    title: 'Maintenance',
    boardOnly: 'Maintenance is handled by the board. To report a problem, submit a request.',
    nav: { workOrders: 'Work orders', calendar: 'Calendar', logbook: 'Maintenance logbook', assets: 'Components', vendors: 'Vendors' },
    trades: {
      general: 'General contractor', plumbing: 'Plumbing', electrical: 'Electrical', hvac: 'Heating and ventilation', roofing: 'Roofing',
      elevator: 'Elevator', fire_safety: 'Fire safety', envelope: 'Envelope (façade, windows)', landscaping: 'Landscaping',
      snow_removal: 'Snow removal', cleaning: 'Cleaning', pest_control: 'Pest control', locksmith: 'Locksmith', painting: 'Painting',
      professional: 'Professional (engineer, architect…)', other: 'Other',
    },
    categories: {
      structure: 'Structure', envelope: 'Envelope (façade, windows)', roofing: 'Roofing', plumbing: 'Plumbing', electrical: 'Electrical',
      hvac: 'Heating and ventilation', elevator: 'Elevator', fire_safety: 'Fire safety', parking: 'Parking',
      exterior: 'Exterior', common_areas: 'Common areas', other: 'Other',
    },
    conditions: { good: 'Good', fair: 'Fair', poor: 'Poor', unknown: 'Unknown' },
    statuses: { open: 'To assign', assigned: 'Assigned', scheduled: 'Scheduled', completed: 'Completed', cancelled: 'Cancelled' },
    taskKinds: { legal: 'Legal obligation', insurance: 'Insurance', preventive: 'Preventive maintenance' },
    taskStatuses: { overdue: 'Overdue', due_soon: 'Due soon', ok: 'Up to date', unscheduled: 'To schedule', inactive: 'Deactivated' },
    logbookKinds: { inspection: 'Inspection', maintenance: 'Maintenance', repair: 'Repair', replacement: 'Replacement', study: 'Study or report', correction: 'Correction', other: 'Other' },
    urgent: 'Urgent',
    normal: 'Normal',
    number: (n: number): string => `WO #${n}`,
    newWorkOrder: 'New work order',
    workOrderTitle: 'Title',
    description: 'Description',
    priority: 'Priority',
    asset: 'Component',
    vendor: 'Vendor',
    scheduledOn: 'Scheduled for',
    estimate: 'Estimate',
    actualCost: 'Actual cost',
    status: 'Status',
    noWorkOrders: 'No open work orders.',
    showAll: 'Also show completed work orders',
    fromRequest: (n: number): string => `Request #${n}`,
    seeRequest: 'See the request',
    complete: 'Mark as completed',
    completedOn: 'Completed on',
    completionNote: 'What was done',
    logbookKind: 'Type of work (logbook)',
    completeHint: 'An entry will be added to the maintenance logbook.',
    cancelOrder: 'Cancel work order',
    closed: 'This work order is closed.',
    emailVendor: 'Email the vendor',
    none: '—',
    calendarIntro: "The syndicate's legal obligations, insurance and preventive maintenance, with their due dates.",
    addStandard: 'Add the standard calendar',
    addStandardHint: 'Contingency fund study, maintenance logbook, insurance, fire safety inspections… You will adjust the dates afterwards.',
    standardAdded: (n: number): string => (n === 0 ? 'The standard calendar is already in place.' : `${n} obligations added.`),
    addTask: 'Add an obligation',
    taskTitle: 'Obligation',
    kind: 'Type',
    every: 'Frequency',
    everyMonths: (n: number | null): string => (n === null ? 'Once' : n % 12 === 0 ? (n === 12 ? 'Every year' : `Every ${n / 12} years`) : `Every ${n} months`),
    monthsLabel: 'Frequency in months',
    nextDue: 'Next due',
    lastDone: 'Last done',
    reference: 'Reference',
    daysLeft: (d: number): string => (d < 0 ? `${-d} days overdue` : d === 0 ? 'Today' : `In ${d} days`),
    markDone: 'Done',
    doneOn: 'Done on',
    doneNote: 'Note (report, company…)',
    cost: 'Cost',
    setDate: 'Set the date',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    noTasks: 'No obligations in the calendar.',
    groups: { overdue: 'Overdue', due_soon: 'In the next 60 days', unscheduled: 'To schedule', ok: 'Up to date', inactive: 'Deactivated' },
    logbookIntro: "The syndicate's maintenance logbook: every inspection, repair or replacement, in order. Entries are never edited; a correction is added.",
    logbookProfessional: 'The official logbook is established and revised by a member of a professional order; this record feeds it.',
    addEntry: 'Add an entry',
    entryDate: 'Date',
    entryTitle: 'Work done',
    allAssets: 'All components',
    noEntries: 'No logbook entries.',
    recordedBy: (name: string): string => `Recorded by ${name}`,
    print: 'Print',
    assetsIntro: 'Components of the common portions, their age and expected life: the basis of the maintenance logbook and the contingency fund study.',
    addAsset: 'Add a component',
    assetName: 'Component',
    category: 'Category',
    location: 'Location',
    installedOn: 'Installed on',
    lifeYears: 'Expected life (years)',
    endOfLife: 'Expected end of life',
    replacementCost: 'Replacement cost',
    condition: 'Condition',
    notes: 'Notes',
    noAssets: 'No components recorded.',
    edit: 'Edit',
    history: 'History',
    vendorsIntro: "The syndicate's vendors. Check the RBQ licence and insurance certificate before assigning work.",
    addVendor: 'Add a vendor',
    vendorName: 'Name',
    trade: 'Trade',
    contactName: 'Contact',
    email: 'Email',
    phone: 'Phone',
    rbq: 'RBQ licence',
    insuranceExpires: 'Insurance valid until',
    insuranceExpired: 'Insurance expired',
    insuranceSoon: 'Insurance to renew',
    active: 'Active',
    inactive: 'Inactive',
    noVendors: 'No vendors recorded.',
    dashboardTitle: 'Maintenance and compliance',
    overdueTodo: (n: number): string => (n === 1 ? '1 overdue obligation' : `${n} overdue obligations`),
    dueSoonTodo: (n: number): string => (n === 1 ? '1 obligation due within 60 days' : `${n} obligations due within 60 days`),
    urgentTodo: (n: number): string => (n === 1 ? '1 urgent work order' : `${n} urgent work orders`),
    vendorReportsTodo: (n: number): string => (n === 1 ? '1 work order: vendor update to review' : `${n} work orders: vendor updates to review`),
    vendorInsuranceTodo: (n: number): string => (n === 1 ? "1 vendor's insurance is expiring" : `${n} vendors' insurance is expiring`),
    calendarEmpty: 'The calendar of obligations is not set up yet',
    openOrders: (n: number): string => (n === 1 ? '1 open work order' : `${n} open work orders`),
    upToDate: 'Calendar up to date.',
    requestOrders: 'Work orders',
    createFromRequest: 'Create a work order',
    proposalTitle: 'Proposed work order',
    proposalHint: 'Prepared by the agent from the request. Nothing is created or sent without you.',
    useProposal: 'Create this work order',
    dismissProposal: 'Dismiss',
    proposalDismissed: 'Work order proposal dismissed.',
    vendorMessage: 'Proposed message to the vendor',
    copy: 'Copy',
    copied: 'Copied',
    why: 'Why',
    noVendorFound: 'No vendor of this trade in your list',
  },
  governance: {
    title: 'Meetings',
    intro: "Co-owners' meetings and board meetings: agenda, notice of meeting, notes, minutes and follow-ups.",
    introNpo: "Members' meetings and board meetings: agenda, notice of meeting, notes, minutes and follow-ups.",
    boardOnly: 'Meetings are prepared by the board. Approved minutes are in Documents.',
    kinds: { annual: 'Annual meeting', special: 'Special meeting', board: 'Board meeting' },
    statuses: { draft: 'In preparation', convened: 'Convened', held: 'Held', closed: 'Closed', cancelled: 'Cancelled' },
    itemKinds: { procedure: 'Procedure', information: 'Information', discussion: 'Discussion', resolution: 'Resolution', election: 'Election' },
    majorities: { simple: 'Simple majority', absolute: 'Absolute majority', double: 'Double majority', triple: 'Triple majority', unanimous: 'Unanimity' },
    sources: { statutory: 'Standard item', request: 'Request', obligation: 'Obligation', work_order: 'Work order', follow_up: 'Follow-up', proposal: 'Proposal', manual: 'Added' },
    newMeeting: 'New meeting',
    chair: 'Chair',
    signTodo: (title: string): string => `Written resolution to sign: ${title}`,
    makeChair: 'Make chair',
    castingUsed: 'casting vote',
    repeat: 'Repeat',
    repeats: { none: 'Once', week: 'Every week', two_weeks: 'Every two weeks', month: 'Every month', two_months: 'Every two months', quarter: 'Every quarter' },
    count: 'Number of meetings',
    seriesPreview: (dates: string): string => `Dates: ${dates}`,
    seriesCreated: (n: number): string => `${n} meetings created.`,
    sections: { meetings: 'Meetings', written: 'Written resolutions', registers: 'Registers' },
    written: {
      intro: 'A written resolution signed by all the directors is as valid as if adopted at a board meeting (C.c.Q., art. 337; check your by-laws).',
      new: 'New written resolution',
      title: 'Title',
      text: 'Text of the resolution',
      textPlaceholder: 'Il est résolu que… (the syndicate’s records are kept in French)',
      none: 'No written resolution.',
      statuses: { draft: 'Draft', circulating: 'Being signed', adopted: 'Adopted', cancelled: 'Cancelled' },
      circulate: 'Send to the directors for signature',
      confirmCirculate: 'Send this resolution to every director in office? The text can no longer be changed.',
      sign: 'I sign this resolution',
      confirmSign: 'Sign this resolution? Your signature is dated and cannot be withdrawn.',
      cancel: 'Cancel the resolution',
      confirmCancel: 'Cancel this resolution?',
      signed: (d: string): string => `Signed on ${d}`,
      waiting: 'Waiting',
      adoptedOn: (d: string): string => `Adopted on ${d}`,
      progress: (n: number, total: number): string => `${n} of ${total} signature${total > 1 ? 's' : ''}`,
      yourTurn: 'Your signature is expected.',
    },
    registers: {
      intro: "The syndicate's register of resolutions and minute book, kept up to date from meetings and written resolutions.",
      resolutions: 'Register of resolutions',
      scope: { board: 'Board of directors', assembly: 'Assemblies' },
      number: 'No.',
      date: 'Date',
      source: 'Source',
      resolution: 'Resolution',
      decision: 'Decision',
      written: 'Written resolution',
      none: 'No resolution in the register.',
      minuteBook: 'Minute book',
      minuteBookNone: 'No approved minutes.',
      open: 'Open',
      print: 'Print',
      rejected: 'Rejected',
    },
    kind: 'Type',
    meetingTitle: 'Title',
    date: 'Date',
    time: 'Time',
    location: 'Place',
    upcoming: 'Upcoming',
    past: 'Past',
    none: 'No meetings.',
    back: 'All meetings',
    steps: { agenda: 'Agenda', notice: 'Notice of meeting', meeting: 'Meeting', minutes: 'Minutes' },
    agendaHint: 'The standard items are already in place. Add the topics of this meeting, or let the agent propose them.',
    addItem: 'Add an item',
    itemTitle: 'Item',
    description: 'Context',
    itemKind: 'Nature',
    resolution: 'Proposed resolution',
    majority: 'Required majority',
    up: 'Up',
    down: 'Down',
    remove: 'Remove',
    edit: 'Edit',
    draftAgenda: 'Prepare with the agent',
    draftingAgenda: 'The agent is reviewing requests, deadlines, work and follow-ups…',
    proposalsTitle: 'Items proposed by the agent',
    proposalsHint: 'From what is pending elsewhere in Syndiqia. Add the ones you keep.',
    noProposals: 'Nothing pending that should go on this agenda.',
    add: 'Add',
    added: 'Added',
    convenedEdit: 'The notice was sent with this agenda: changes will have to be announced at the meeting.',
    noticeRule: (min: number | null, max: number | null): string =>
      min === null ? 'No required notice period.' : max === null ? `To send at least ${min} days before the meeting.` : `To send between ${min} and ${max} days before the meeting.`,
    noticeDays: (d: number): string => (d < 0 ? 'The meeting date has passed.' : `The meeting is in ${d} day${d > 1 ? 's' : ''}.`),
    outsidePeriod: 'Outside the usual period: check the declaration or by-laws.',
    ruleToValidate: 'Default periods, to validate against the declaration of co-ownership or by-laws.',
    prepareNotice: 'Prepare the notice of meeting',
    noticeRecipients: (n: number): string => `${n} recipient${n > 1 ? 's' : ''}`,
    noticeUnreachable: (n: number): string => `${n} ${n > 1 ? 'people have' : 'person has'} no email: convene them another way (and keep proof).`,
    noticePending: 'Notice ready, awaiting approval by a director.',
    approveSend: (n: number): string => `Approve and send (${n} email${n > 1 ? 's' : ''})`,
    reject: 'Reject',
    noticeSent: (n: number, date: string): string => `Notice sent on ${date} to ${n} recipient${n > 1 ? 's' : ''}, with proof of delivery.`,
    noticeRejected: 'The notice was rejected. Edit the agenda, then prepare it again.',
    preview: 'Email preview',
    markConvened: 'Convened another way',
    generalNotes: 'Attendance, quorum, chair and secretary',
    generalNotesHint: 'E.g.: 18 co-owners present or represented (62% of votes); chair: …; secretary: …',
    notes: 'Notes',
    decision: 'Decision',
    decisionPlaceholder: 'E.g.: Adopted unanimously',
    saved: 'Saved',
    markHeld: 'Mark the meeting as held',
    cancelMeeting: 'Cancel the meeting',
    draftMinutes: 'Draft the minutes with the agent',
    redraftMinutes: 'Draft again with the agent',
    draftingMinutes: 'The agent is drafting the minutes from your notes…',
    minutesHint: 'The agent invents nothing: missing facts are marked [À compléter]. Review, correct, then approve.',
    minutesText: 'Minutes',
    saveMinutes: 'Save',
    approveMinutes: 'Approve and publish to Documents',
    approvedOn: (date: string): string => `Approved on ${date}.`,
    publishedNote: 'Published to Documents.',
    seeDocument: 'See the document',
    actions: 'Follow-ups',
    actionsHint: 'Open follow-ups are proposed for the agenda of the next meeting.',
    addAction: 'Add a follow-up',
    actionDescription: 'Follow-up',
    responsible: 'Responsible',
    dueOn: 'Due',
    done: 'Done',
    reopen: 'Reopen',
    noActions: 'No follow-ups.',
    openActions: 'Open follow-ups',
    noticeTodo: (title: string): string => `Notice of meeting to approve: ${title}`,
    minutesTodo: (title: string): string => `Minutes to approve: ${title}`,
    upcomingTodo: (title: string, days: number): string => `${title} in ${days} day${days > 1 ? 's' : ''}: notice of meeting to send`,
    aiDisabled: 'The agents are not enabled.',
    attendance: 'Attendance register',
    attendanceHint: 'Prepared from the register on the meeting date: each co-owner once, with their votes (quote-parts).',
    attendanceHintLots: 'Prepared from the register on the meeting date: each owner once, one vote per lot.',
    attendanceHintPersons: 'Prepared from the register on the meeting date: each member once, one vote each.',
    attendanceHintBoard: 'The directors, one vote each.',
    prepareAttendance: 'Prepare the attendance register',
    refreshAttendance: 'Prepare again',
    present: 'Present',
    proxy: 'Proxy',
    absent: 'Absent',
    proxyHolder: 'Proxy holder',
    votesLabel: 'Votes',
    noVote: 'No right to vote',
    search: 'Search a name or unit',
    quorumLine: (pct: string, present: number, total: number): string => `${present} of ${total} present or represented · ${pct} of votes`,
    quorumReached: 'Quorum reached',
    quorumNotReached: 'Quorum not reached',
    allPresent: 'All present',
    vote: 'Vote',
    unanimous: 'Unanimous',
    detailedVote: 'Detailed vote',
    allFor: 'All for',
    for: 'For',
    against: 'Against',
    abstain: 'Abst.',
    tally: (forPct: string, againstPct: string, abstainPct: string): string => `For ${forPct} · Against ${againstPct} · Abstentions ${abstainPct}`,
    eligibleLine: (p: string): string => `votes present or represented entitled to vote: ${p}`,
    wouldPass: 'Would be adopted',
    wouldFail: 'Would be rejected',
    closeVote: 'Close the vote',
    voteClosed: 'Vote closed',
    needAttendance: 'Prepare the attendance register to record votes.',
    rulesToValidate: 'Majorities computed per the Civil Code (arts. 1089, 1096–1098), to validate against the declaration.',
    memberTitle: 'Meetings',
    memberIntro: 'The meetings you are convened to, with their agenda. Approved minutes are in Documents.',
    memberNone: 'No meeting convened at the moment.',
    minutesInDocuments: 'Minutes available in Documents',
    recording: 'Meeting recording',
    recordingHint: 'The recording is transcribed automatically to draft the minutes. The audio is deleted once transcribed, and the transcript once the minutes are approved.',
    consent: 'To read at the opening: “The meeting is recorded to help draft the minutes. The recording will be deleted once transcribed. If anyone objects, we can stop recording.”',
    startRecording: 'Start recording',
    pause: 'Pause',
    resume: 'Resume',
    nextItem: 'Next item',
    finishRecording: 'Stop recording',
    recordingNow: (item: string): string => `Recording · ${item}`,
    pausedNow: 'Paused',
    uploaded: (mb: string): string => `${mb} MB uploaded`,
    uploadRetry: 'Upload interrupted, retrying…',
    micDenied: 'The microphone is not available. Allow it in the browser.',
    notSupported: 'This browser cannot record. Use a recent Chrome, Edge, Firefox or Safari.',
    leaveWarning: 'A recording is in progress.',
    importRecording: 'Import a recording or a transcript',
    importHint: 'An audio file (MP3, M4A, WAV, WebM) from Teams, Zoom or a recorder, or the transcript Teams or Zoom produce (.vtt, .txt).',
    importing: 'Uploading…',
    recordingStatuses: { recording: 'Recording', uploaded: 'Waiting for transcription', transcribing: 'Transcribing…', transcribed: 'Transcribed', failed: 'Failed', purged: 'Deleted' },
    recordingSources: { browser: 'Recorded here', upload: 'Imported file', transcript: 'Imported transcript' },
    retry: 'Retry',
    transcript: 'Transcript',
    speakers: 'Speakers',
    speakersHint: 'Give each voice a name or role (e.g. Chair). They will be used in the minutes.',
    speakerLabel: (n: number): string => `Speaker ${n}`,
    duration: (min: number): string => `${min} min`,
    transcriptUsed: 'The transcript will be used to draft the minutes.',
  },
  reservations: {
    title: 'Reservations',
    intro: "Book the common facilities for your unit. Each facility's rules apply automatically.",
    kinds: { room: 'Room', gym: 'Gym', court: 'Court', elevator: 'Elevator', pool: 'Pool', bbq: 'BBQ', guest_suite: 'Guest suite', other: 'Other' },
    statuses: { requested: 'Awaiting approval', confirmed: 'Confirmed', cancelled: 'Cancelled', rejected: 'Rejected' },
    tabs: { book: 'Book', mine: 'My bookings', all: 'All bookings', facilities: 'Facilities' },
    noFacilities: 'No facilities to book at the moment.',
    hours: (from: string, to: string): string => `From ${from} to ${to}`,
    duration: (min: number, max: number): string => `${min < 60 ? `${min} min` : `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60}` : ''}`} to ${max < 60 ? `${max} min` : `${Math.floor(max / 60)} h${max % 60 ? ` ${max % 60}` : ''}`}`,
    fee: (amount: string): string => `Fee: ${amount}`,
    free: 'Free',
    deposit: (amount: string): string => `Deposit: ${amount}`,
    quota: (n: number): string => `${n} booking${n > 1 ? 's' : ''} per week per unit`,
    approval: 'Board approval required',
    cancelRule: (h: number): string => `Free cancellation up to ${h} h before`,
    ahead: (d: number): string => `Up to ${d} days ahead`,
    day: 'Date',
    start: 'Start',
    length: 'Duration',
    unit: 'Unit',
    notes: 'Note (optional)',
    book: 'Book',
    bookFee: (amount: string): string => `Book (${amount} charged to your account)`,
    request: 'Request the booking',
    taken: 'Booked',
    yours: 'You',
    closed: 'Closed',
    free_: 'Free',
    booked: 'Booking confirmed.',
    requested: 'Request sent to the board.',
    rules: {
      order: 'The end must follow the start.',
      same_day: 'The booking must start and end on the same day.',
      hours: 'Outside opening hours.',
      slot: 'Choose a proposed start time and duration.',
      too_short: 'Too short.',
      too_long: 'Too long.',
      notice: 'Not enough notice.',
      too_far: 'Too far ahead.',
      blackout: 'The facility is closed at that time.',
      quota: "This unit's bookings for the week are used up.",
      overlap: 'This time is already booked.',
    },
    noUnit: 'No unit is linked to your account: ask the board to link you to your unit.',
    upcoming: 'Upcoming',
    none: 'No upcoming bookings.',
    cancel: 'Cancel',
    cancelUntil: (when: string): string => `Free cancellation until ${when}`,
    cancelLate: 'Cancellation deadline passed: contact the board.',
    approve: 'Approve',
    reject: 'Reject',
    bookedBy: (name: string): string => `by ${name}`,
    addFacility: 'Add a facility',
    edit: 'Edit',
    name: 'Name',
    kind: 'Type',
    description: 'Description',
    location: 'Location',
    opensAt: 'Opens',
    closesAt: 'Closes',
    slotMinutes: 'Step (minutes)',
    minMinutes: 'Minimum duration (min)',
    maxMinutes: 'Maximum duration (min)',
    maxDaysAhead: 'Days ahead (max)',
    minNoticeHours: 'Minimum notice (hours)',
    cancelNoticeHours: 'Free cancellation (hours before)',
    weeklyQuota: 'Bookings per week per unit',
    feeLabel: 'Fee',
    depositLabel: 'Deposit',
    requiresApproval: 'Board approval required',
    rulesText: 'Rules shown to members',
    active: 'Available for booking',
    inactive: 'Not available',
    blackouts: 'Closures',
    addBlackout: 'Add a closure',
    from: 'From',
    to: 'To',
    reason: 'Reason',
    remove: 'Remove',
    requestsTodo: (n: number): string => (n === 1 ? '1 booking to approve' : `${n} bookings to approve`),
  },
  members: {
    back: 'Co-owners and contacts',
    details: 'Contact details',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    language: 'Language',
    mailingAddress: 'Mailing address',
    edit: 'Edit',
    saved: 'Saved.',
    contactNote: 'This email is used for notices. The sign-in email stays the account’s.',
    units: 'Units',
    owned: 'Owner',
    occupied: 'Occupant',
    share: (n: number, d: number): string => (n === d ? '' : ` (${n}/${d})`),
    since: (d: string): string => `since ${d}`,
    period: (from: string, to: string): string => `from ${from} to ${to}`,
    noUnits: 'No units.',
    access: 'Access and roles',
    noAccount: 'No account yet.',
    roles: 'Roles in this syndicate',
    makeDirector: 'Make a director',
    removeDirector: 'Remove from the board',
    makeManager: 'Make a manager',
    removeManager: 'Remove the manager role',
    roleNote: 'Note (e.g. elected at the AGM of 25 March 2026)',
    confirmRole: (name: string, role: string, granted: boolean): string => (granted ? `Give the "${role}" role to ${name}?` : `Remove the "${role}" role from ${name}?`),
    inviteFirst: 'Invite the person first: roles are given to an account.',
    history: 'Role history',
    granted: 'appointed',
    removed: 'removed',
    by: (name: string): string => `by ${name}`,
    occupants: 'Occupants (tenants)',
    noOccupants: 'No occupants recorded.',
    addOccupant: 'Add an occupant',
    person: 'Person',
    from: 'From',
    endOccupancy: 'End the occupancy',
    endOn: 'Last day',
    occupantHint: "An occupant can submit requests and book for the unit; they do not see the owner's account.",
    profileTitle: 'My profile',
    profileIntro: 'Your contact details in this syndicate. The board uses them for notices and communications.',
    noRecord: 'Your account is not linked to a record of this syndicate yet. Ask the board.',
    myUnits: 'My units',
    digestTitle: 'Email summary',
    digestIntro: 'What the agents did on their own and what waits for the board, sent only when there is something.',
    digestOptions: { daily: 'Every day', weekly: 'Every Monday', off: 'Never' },
    loginEmail: 'Sign-in email',
    loginEmailIntro: (email: string) => `You sign in with ${email}. To change it, enter the new address: a confirmation link will be sent there.`,
    newLoginEmail: 'New address',
    changeLoginEmail: 'Change',
    loginEmailPending: (email: string) => `A confirmation link was sent to ${email}. Use this address to sign in from now on.`,
    loginEmailHint: 'This is your account address (for all your syndicates); your contact details above may differ.',
  },
  help: {
    title: 'Help',
    short: '?',
    intro: 'The Syndiqia user guide: the tasks that concern you, according to your role.',
    search: 'Search the guide…',
    results: 'Results',
    noResults: 'No topic found. Try other words, or ask the question on the home page.',
    onThisPage: (page: string) => `On this page: ${page}`,
    askHint: 'Can’t find it? Ask your question on the home page: the answer comes from this guide and the syndicate’s documents.',
    askLink: 'Go to the home page',
    guide: 'Syndiqia guide',
    pages: {
      start: 'Getting started', home: 'Home', profile: 'My profile', requests: 'Requests', documents: 'Documents', units: 'Units',
      parties: 'Co-owners and roles', 'quote-parts': 'Shares', finances: 'Finances', approvals: 'Approvals', maintenance: 'Maintenance',
      governance: 'Assemblies', reservations: 'Reservations', insurance: 'Insurance', automation: 'Automation', onboarding: 'Onboarding', settings: 'Settings',
    },
  },
  automation: {
    title: 'Automation',
    intro: 'For each process, you choose how much the agents do on their own. At first, everything waits for your confirmation; once you trust them, switch a process to automatic. Everything done automatically is logged below.',
    boardOnly: 'Automation is set up by the board.',
    directorsOnly: 'Only a director can change the mode of a process.',
    levels: { confirm: 'Confirmation', auto: 'Automatic' },
    guardrails: 'Guardrails',
    default: 'Initial setting: confirmation by a person',
    changed: (d: string, who: string) => `Changed on ${d} by ${who}`,
    recent: (n: number) => (n === 0 ? 'no automatic action in the last 30 days' : `${n} automatic action${n > 1 ? 's' : ''} in the last 30 days`),
    confirm: (process: string, level: string) => `Switch "${process}" to ${level.toLowerCase()}? Note for the history (optional):`,
    noDecisions: 'No board decisions yet on what the agent prepared.',
    accuracy: (agreed: number, total: number) => `Accuracy (90 days): the board confirmed ${agreed} of ${total} proposal${total > 1 ? 's' : ''} as they were (${Math.round((agreed / total) * 100)}%).`,
    ready: 'Ready for automatic mode',
    integrityTitle: 'Log integrity',
    integrityIntro: 'Every event and every automatic action is chained to the previous one by a fingerprint (SHA-256): changing, removing or inserting a row afterwards shows. Every day, the latest fingerprint is also stored outside the database.',
    integrityCheck: 'Check integrity',
    integrityOk: (events: number, actions: number): string => `Log intact: ${events} event(s) and ${actions} automatic action(s) verified.`,
    integrityBroken: 'The log has been altered.',
    integrityBrokenAt: (stream: string, seq: number): string => `${stream}: break at position ${seq}.`,
    integrityStreams: { outbox: 'Events', automation_log: 'Automatic actions' },
    integrityAnchorOk: (day: string): string => `Matches the fingerprint stored on ${day}.`,
    integrityAnchorMismatch: (day: string): string => `Does not match the fingerprint stored on ${day}: the history was rewritten or reset since.`,
    integrityNoAnchor: 'No external fingerprint in the last 7 days (one is stored daily).',
    limitLabel: 'Amount authorized without a quote',
    limitNone: 'to set before automatic mode',
    limitSave: 'Save the amount',
    limitShort: (amount: string) => `up to ${amount}`,
    processes: {
      'reservations.approve': {
        title: 'Booking approvals',
        confirm: 'bookings of facilities that require it wait for the board’s approval.',
        auto: 'a booking that meets the rules is approved right away and the person gets their confirmation.',
        guardrails: 'only if the unit owes nothing more than 30 days overdue; the facility’s rules (notice, quotas, closures, overlaps) always apply; a refusal is always a board decision',
      },
      'maintenance.dispatch': {
        title: 'Sending work to contractors',
        confirm: 'the agent proposes a work order and a contractor for each reported repair; the board creates it and sends the link.',
        auto: 'the work order is created and the contractor receives the link right away, with the authorized amount; above it, they must send a quote.',
        guardrails: 'non-urgent work only (an emergency stays human), active contractor reachable by email, insurance in force and RBQ licence for trades that require it; never if a work order already exists for the request; no payment',
      },
      'maintenance.invoices': {
        title: 'Supplier invoice approvals',
        confirm: 'the agent reads each invoice and checks it against the work order; the board approves or rejects it.',
        auto: 'a compliant invoice is approved right away: the expense and the amount owed to the contractor are recorded in the books.',
        guardrails: 'only an invoice read with confidence, with no discrepancy (right contractor, work completed, amount within the estimate, number not repeated, correct GST/QST) and under the set amount; recorded to building maintenance in the operating fund; no payment: paying stays a bank operation by the board',
        limitLabel: 'Maximum amount of an invoice approved automatically',
      },
      'requests.reply': {
        title: 'Replies to owners’ requests',
        confirm: 'the agent drafts a reply from the documents; a board member checks and sends it.',
        auto: 'the reply is sent right away when it is safe, marked "automatic reply".',
        guardrails: 'information questions only, answer backed by a cited article, high confidence; never what needs a board decision (authorization, complaint, work, money), never an emergency, never if someone already replied',
      },
      'collections.reminders': {
        title: 'Payment reminders',
        confirm: 'each batch of reminders waits for a director’s approval.',
        auto: 'reminders go out at 15 and 30 days late, without approval.',
        guardrails: 'never while bank deposits remain unreconciled; people who paid meanwhile are removed; after two reminders the file goes to the board; no formal notice or legal action',
      },
      'ledger.bank_match': {
        title: 'Bank reconciliation',
        confirm: 'Syndiqia suggests a match for each imported transaction; the board confirms.',
        auto: 'sure matches are posted at import; the others wait for the board.',
        guardrails: 'only high-confidence matches (exact amount and recognized reference); a mistake is corrected with a reversing entry',
      },
      'insurance.review': {
        title: 'Insurance certificate review',
        confirm: 'the board accepts or rejects each certificate received.',
        auto: 'a compliant certificate is accepted as soon as it is received.',
        guardrails: 'read with high confidence and not changed by the person, insurer and policy present, amount at least the required minimum, in force today; a rejection is always a board decision',
      },
    },
    alwaysHumanTitle: 'Always a human decision',
    alwaysHumanIntro: 'These decisions cannot be automated, whatever the setting.',
    alwaysHuman: [
      'Money going out and paying suppliers (automatic dispatch pays nothing)',
      'Fines and by-law enforcement',
      'Special assessments and contingency fund strategy',
      'Notices of meeting and other legal notices',
      'Formal notices, disputes and court proceedings',
      'Emergencies (never sent automatically to a contractor)',
    ],
    logTitle: 'Automatic actions',
    logNone: 'No automatic action yet.',
    historyTitle: 'Settings history',
  },
  board: {
    composition: 'Board composition',
    titlesHeading: 'Board positions',
    titles: { president: 'President', vice_president: 'Vice-president', secretary: 'Secretary', treasurer: 'Treasurer', director: 'Director' },
    until: (d: string) => `until ${d}`,
    noEnd: 'no end date',
    endingSoon: 'Term to renew',
    noTitle: 'No position recorded.',
    past: 'ended',
    title: 'Position',
    startsOn: 'Term start',
    endsOn: 'Term end',
    endsOnHint: 'Often the next annual meeting',
    note: 'Note',
    notePlaceholder: 'e.g. elected at the May 12 meeting',
    appoint: 'Add the position',
    endToday: 'End today',
    delete: 'Delete',
    confirmEnd: (title: string, name: string) => `End ${name}'s term as ${title} today?`,
    confirmDelete: 'Delete this position (entered by mistake)? For a term that ended, use "End" instead.',
  },
  purchasing: {
    quotes: 'Quotes',
    quotesHint: 'Invite up to 5 contractors: each gets their own link, no account needed, to give their price (taxes extra), their timing and attach their quote. Retaining a quote assigns the work to that contractor and tells the others.',
    invite: 'Request quotes',
    inviteSend: 'Send the requests',
    dueOn: 'Answer wanted by',
    noEmail: 'no email',
    invited: (n: number): string => `${n} request(s) sent.`,
    vendor: 'Contractor',
    amount: 'Price (before taxes)',
    delay: 'Timing',
    days: (n: number): string => `${n} day(s)`,
    validUntil: 'Valid until',
    exclusions: 'Exclusions read',
    file: 'Document',
    retain: 'Retain',
    retained: (email: string | null): string => (email ? `Quote retained. The work order is assigned to the contractor and their link sent to ${email}.` : 'Quote retained.'),
    quoteStatuses: { requested: 'Waiting', received: 'Received', declined_by_vendor: 'Declined', selected: 'Retained', not_selected: 'Not retained' },
    lowest: 'lowest',
    invoices: 'Invoices',
    invoicesHint: 'The agent reads each invoice (PDF) and Syndiqia checks it against the work order. Approving records the expense and the amount owed to the contractor in the books; payment is made at the bank, then reconciled.',
    addInvoice: 'Add an invoice received',
    addInvoiceHint: 'PDFs are read automatically. For a photo or a scanned PDF, enter the amounts.',
    number: 'Invoice no.',
    date: 'Date',
    subtotal: 'Subtotal',
    taxes: 'GST + QST',
    total: 'Total',
    source: { vendor: 'through the contractor’s link', board: 'added by the board', email: 'received by email' },
    invoiceStatuses: { received: 'To approve', approved: 'Approved', rejected: 'Rejected' },
    clean: 'Matches the work order',
    issues: {
      vendor_mismatch: 'Not the work order’s contractor',
      vendor_name_mismatch: 'The name on the invoice does not match the contractor',
      not_completed: 'The work order is not completed',
      no_estimate: 'No authorized estimate to compare with',
      over_estimate: 'Exceeds the estimate by more than 10%',
      duplicate_number: 'Invoice number already received from this contractor',
      tax_arithmetic: 'GST/QST do not match the subtotal',
      total_arithmetic: 'Subtotal + taxes ≠ total',
      unread: 'File not read: check the amounts entered',
      missing_total: 'Total not readable: enter it before approving',
    },
    overBy: (amount: string): string => ` (by ${amount})`,
    expectedTaxes: (amount: string): string => ` (expected: ${amount})`,
    account: 'Expense account',
    fund: 'Fund',
    approve: 'Approve',
    reject: 'Reject',
    rejectReason: 'Reason (visible to the contractor)',
    edit: 'Correct the amounts',
    save: 'Save',
    view: 'View the invoice',
    toApprove: (n: number): string => `${n} contractor invoice(s) to approve`,
    toFile: (n: number): string => `${n} invoice(s) received by email to sort`,
    toFileTitle: (n: number): string => `Invoices to sort (${n})`,
    toFileHint: 'Received at the billing address without a recognized work order. Choose the work order: the invoice is then checked and waits for your approval.',
    workOrder: 'Work order',
    chooseWorkOrder: 'Choose the work order…',
    suggested: 'Suggestions',
    allWorkOrders: 'All work orders',
    suggestionReasons: { vendor: 'same contractor', amount: 'close amount', recent: 'recent' },
    fileIt: 'Sort',
    notInvoice: 'Not an invoice',
    totalUnknown: 'total to enter',
    matchedBy: { number: 'work order number on the invoice', only_job: 'this contractor’s only current job' },
    invoiceAddressHint: (address: string): string => `Contractors send their invoices to ${address}: they are read and matched to the work order automatically.`,
    toCompare: (n: number): string => `${n} work order(s): quotes received to compare`,
    quoteTitle: 'Request for quote',
    quoteIntro: 'Give your price before taxes and your timing, and attach your quote. You can change it until the board decides.',
    quoteAmount: 'Price before taxes ($)',
    quoteDelay: 'Timing (days)',
    quoteValid: 'Price valid until',
    quoteFile: 'Quote (PDF or photo)',
    quoteSend: 'Send my quote',
    quoteDecline: 'I will not quote',
    quoteSent: 'Quote received. Thank you! The board will let you know its decision.',
    yourQuote: 'Your quote',
    sendInvoice: 'Send my invoice',
    invoiceHint: 'PDF preferred. The board checks and approves it; payment follows the agreed terms.',
    invoiceNumber: 'Invoice no. (optional if readable in the PDF)',
    invoiceTotal: 'Total with taxes ($) (optional if readable)',
    yourInvoices: 'Your invoices',
    closedWork: 'This work is completed. You can still send your invoice.',
    billing: 'Billing:',
    invoiceByEmail: (address: string, n: number): string => `email your invoice to ${address}, mentioning "work order no. ${n}". You can also upload it here.`,
    uploadInvoice: 'Upload an invoice',
  },
  onboarding: {
    title: 'Onboarding',
    toReview: (n: number) => `Onboarding: ${n} step(s) to review and apply`,
    toStart: 'Onboarding: send us your documents, owners list and finances',
    intro: 'Give us what you have, topic by topic, in whatever format you have: PDF, Word, Excel, CSV. Syndiqia reads everything, tells you what is complete and what is missing, and shows you what it understood. Nothing is saved until you click "Apply".',
    existing: (lots: number, parties: number, documents: number) => `Already in Syndiqia: ${lots} lot(s), ${parties} people, ${documents} document(s).`,
    statuses: { empty: 'To do', reading: 'Reading…', ready: 'To review', applied: 'Done', skipped: 'Skipped' },
    consoleIntro: (name: string) => `Onboarding of ${name}. Each phase is saved as soon as it is done: you can stop and resume later from the console. The last phase invites the board of directors and activates the client.`,
    previous: 'Previous phase',
    next: 'Next phase',
    saveQuit: 'Save and go back to the console',
    quit: 'Back to home',
    skip: 'Skip this phase for now',
    saveContinue: 'Save and continue',
    identity: { address: 'Main address', city: 'City', postal: 'Postal code', contact: 'Contact person (board chair)', contactName: 'Name', contactEmail: 'Email', contactPhone: 'Phone', lots: 'Number of lots (approximate)', plan: 'Plan', notes: 'Notes', none: '—', plans: { autonome: 'Autonome', assistee: 'Assistée', complete: 'Complète' } },
    board: { emailsNotSent: (emails: string) => `Client activated. The invitation email could not be sent to: ${emails}. While email sending is in test mode at Amazon (SES), only verified addresses receive it; invite these people again from Members once verified.`, intro: 'Choose the directors among the people imported. They receive their invitation (password setup), the client becomes active and the Syndiqia team’s access closes: afterwards, only through a support access the board approves.', search: 'Search for a person', noEmail: 'no email', owner: 'owner', save: 'Save the choice', activate: 'Invite the board and activate the client', confirm: (n: number) => `Invite ${n} director(s) and activate the client? The Syndiqia team’s access will close.`, done: 'Client activated: the directors received their invitation.', noPeople: 'Import the lots and owners first (phase 3).' },
    steps: {
      identity: { title: 'Identification', lookFor: ['Main address (it creates the building or domain)', 'Board contact person', 'Number of lots and plan chosen'] },
      documents: { title: 'Association documents', lookFor: ['Letters patent (or declaration of co-ownership)', 'By-laws and deed of servitudes', 'Minutes of recent meetings', 'Insurance policy, other notices'] },
      members: { title: 'Lots and owners', lookFor: ['The list of lots and their owners (ideally Excel or CSV)', 'Emails, phones, mailing addresses, language', 'Shares, if any'] },
      finances: { title: 'Finances', lookFor: ['The latest financial statements', 'This year’s budget', 'Bank balances (operating, reserve)', 'Amounts owed by lot'] },
      vendors: { title: 'Suppliers and contracts', lookFor: ['Supplier list (name, email, phone)', 'Current contracts: snow removal, maintenance, insurance…'] },
      board: { title: 'Board and activation', lookFor: ['The directors, chosen among the imported owners', 'An email for each'] },
    },
    lookForTitle: 'What we are looking for',
    drop: 'Add files',
    dropHint: 'Several files at once · PDF, Word (.docx), Excel (.xlsx), CSV, text · 20 MB each',
    upload: 'Upload',
    uploading: 'Uploading…',
    files: 'Files received',
    readingHint: 'Syndiqia is reading your files. It usually takes less than a minute.',
    checksTitle: 'What Syndiqia found',
    apply: 'Apply',
    applyHint: 'Saves what is proposed below. You can correct anything later in the app.',
    applyBlocked: 'Complete what is missing (in red) before applying.',
    reset: 'Start this step over',
    applied: 'Applied',
    applyRules: 'Also apply the meeting rules found',
    save: 'Save the correction',
    more: (n: number) => `and ${n} more`,
    preview: {
      document: 'Document', category: 'Category', date: 'Date', visibility: 'Visible to', members: 'Members', board: 'Board', unreadable: 'unreadable (photo or scan?)',
      rules: 'Meeting rules found', voteBasis: { quote_parts: 'vote by shares', lots: 'one vote per lot', persons: 'one vote per member' }, quorum: (n: number) => `quorum ${n}%`, notice: (n: number) => `notice of at least ${n} days`, boardQuorum: (n: number) => `board quorum ${n}%`,
      address: 'Main address', name: 'Name', street: 'Address', city: 'City', postal: 'Postal code',
      lot: 'Lot', owners: 'Owners', email: 'Email', phone: 'Phone', share: 'Share',
      fiscalYear: 'Fiscal year', balancesDate: 'Balances at', operating: 'Operating account', reserve: 'Reserve fund account', investments: 'Investments', arrears: 'Amounts owed', budget: 'Budget', account: 'Account', fund: 'Fund', amount: 'Amount',
      vendor: 'Supplier', trade: 'Trade', service: 'Service',
    },
    categories: { declaration: 'Declaration of co-ownership', letters_patent: 'Letters patent', bylaws: 'By-laws', servitudes: 'Servitudes', minutes: 'Minutes', insurance: 'Insurance', contingency_study: 'Fund study', logbook: 'Maintenance logbook', financial_statements: 'Financial statements', notice: 'Notice', other: 'Other' },
    checks: {
      founding_found: () => 'Founding document received',
      founding_missing: () => 'The founding document is missing (letters patent or declaration of co-ownership)',
      bylaws_found: () => 'By-laws received',
      bylaws_missing: () => 'The by-laws are missing',
      servitudes_found: () => 'Deed of servitudes received',
      servitudes_missing: () => 'No deed of servitudes: add it if there is one',
      minutes_found: (n: number) => `${n} set(s) of minutes received`,
      minutes_missing: () => 'No minutes: add at least the last meeting’s',
      insurance_found: () => 'Insurance policy received',
      rules_found: () => 'Meeting rules found in the by-laws',
      rules_partial: () => 'Meeting rules partly found: check them',
      rules_missing: () => 'Meeting rules not found: the defaults will apply',
      unreadable: (n: number) => `${n} unreadable file(s) (photo or scan without text): send a PDF with text if possible`,
      lots: (n: number) => `${n} lot(s) found`,
      lots_missing: () => 'No lot found in these files',
      owners: (n: number) => `${n} owner(s) found`,
      lots_without_owner: (n: number) => `${n} lot(s) without an owner`,
      no_email: (n: number) => `${n} owner(s) without an email`,
      all_emails: () => 'Every owner has an email',
      no_phone: (n: number) => `${n} owner(s) without a phone`,
      all_phones: () => 'Every owner has a phone',
      duplicate_lots: (n: number) => `${n} duplicate lot(s)`,
      shares_ok: () => 'Shares found for every lot (total 100%)',
      shares_sum: (n: number) => `Shares add up to ${n}% instead of 100%: check`,
      shares_partial: (n: number) => `${n} lot(s) without a share`,
      equal_shares: () => 'No shares in the list: equal shares per lot',
      shares_missing: () => 'No quote-parts in the list: enter them from the declaration',
      address_missing: () => 'The main address is missing: complete it below',
      truncated: () => 'The document holds more lots than could be read: send the list as Excel or CSV',
      fiscal_year: () => 'Fiscal year found',
      fiscal_year_missing: () => 'Fiscal year not found: specify it',
      bank_operating: () => 'Operating account balance found',
      bank_operating_missing: () => 'The operating account balance is missing',
      bank_reserve: () => 'Reserve fund balance found',
      bank_reserve_missing: () => 'No reserve fund balance',
      budget: (n: number) => `Budget found (expenses: $${n.toLocaleString('en-CA')})`,
      budget_missing: () => 'No budget for the year',
      arrears: (n: number) => `${n} amount(s) owed by lots`,
      arrears_unknown_lots: (n: number) => `${n} amount(s) owed on unknown lots`,
      arrears_none: () => 'No amount owed found: is that right?',
      balances_date_missing: () => 'Balances date not found: start of the fiscal year used',
      vendors: (n: number) => `${n} supplier(s) found`,
      vendors_missing: () => 'No supplier found',
      vendors_no_email: (n: number) => `${n} supplier(s) without an email`,
      address_given: () => 'Main address given',
      contact_given: () => 'Contact person given',
      contact_missing: () => 'No contact person email',
      plan_chosen: () => 'Plan chosen',
      plan_missing: () => 'Plan to choose',
      directors: (n: number) => `${n} director(s) chosen`,
      directors_missing: () => 'Choose at least one director',
      directors_no_email: (n: number) => `${n} director(s) without an email: complete their record`,
    } as Record<string, (n: number) => string>,
  },
  vendor: {
    title: (n: number): string => `Work order #${n}`,
    forSyndicate: (name: string): string => `For ${name}`,
    invalid: 'This link is no longer valid (revoked, expired, or closed). Please contact the board.',
    location: 'Location',
    scheduled: 'Scheduled for',
    none: 'To be agreed',
    accept: 'I accept the job',
    decline: 'I cannot do it',
    proposeDate: 'Propose a date',
    date: 'Date',
    addNote: 'Add a message',
    note: 'Message',
    attach: 'Attach a file (quote, invoice, photo)',
    fileHint: 'PDF, JPEG, PNG or HEIC, 20 MB max.',
    done: 'Work completed',
    doneHint: 'The board will confirm after checking.',
    send: 'Send',
    sent: 'Sent. Thank you!',
    history: 'History',
    events: { accepted: 'Job accepted', declined: 'Job declined', date_proposed: 'Date proposed', note: 'Message', file: 'File attached', done: 'Work reported completed' },
    sources: { vendor: 'Vendor', board: 'Board' },
    access: 'For the appointed vendor only. Do not forward this link.',
    panel: 'Vendor',
    noVendor: 'Choose a vendor to give them access to this work order.',
    linkActive: (until: string): string => `Link active until ${until}`,
    linkUsed: (when: string): string => `Last opened on ${when}`,
    linkNever: 'Not opened yet',
    noLink: 'The vendor has no link to this work order yet.',
    sendLink: 'Email the link',
    sentTo: (email: string): string => `Link sent to ${email}.`,
    copyLink: 'Create a link to copy',
    copied: 'Copied',
    revoke: 'Revoke the link',
    linkHint: 'No account needed: the vendor can accept, propose a date, attach a quote, invoice or photos, and report the work done. A new link replaces the previous one.',
    boardNote: 'Message to the vendor',
  },
  insurance: {
    title: "Co-owners' insurance",
    intro: 'Each co-owner must hold civil liability insurance and provide proof to the board. Certificates are personal information: only the board sees them.',
    statuses: { valid: 'Valid', expiring: 'Expiring soon', expired: 'Expired', missing: 'Missing', insufficient: 'Amount too low', submitted: 'To review' },
    certStatuses: { submitted: 'Under review', accepted: 'Accepted', rejected: 'Rejected' },
    roles: { owner: 'Co-owner', occupant: 'Occupant' },
    summary: (missing: number, expired: number, expiring: number): string => `${missing} missing · ${expired} expired · ${expiring} to renew`,
    toReview: 'To review',
    noneToReview: 'No certificates to review.',
    coverage: 'By unit',
    unit: 'Unit',
    person: 'Person',
    insurer: 'Insurer',
    policy: 'Policy no.',
    liability: 'Civil liability',
    startsOn: 'Start',
    expiresOn: 'Expiry',
    file: 'See the certificate',
    accept: 'Accept',
    reject: 'Reject',
    reason: 'Reason (sent to the person)',
    uploadFor: 'Upload for this person',
    requestMissing: 'Request missing certificates',
    requestHint: 'An email to each co-owner without a certificate (at most once a month each).',
    requested: (sent: number, unreachable: number): string => `${sent} email(s) sent${unreachable ? `, ${unreachable} without email` : ''}.`,
    settings: 'Requirements',
    required: 'Minimum civil liability',
    occupantsRequired: 'Occupants must also provide a certificate',
    readByAgent: 'Read automatically from the document: check and correct as needed.',
    mineTitle: 'My insurance',
    mineIntro: (amount: string): string => `Upload your insurance certificate (PDF or photo). Civil liability required: at least ${amount}.`,
    upload: 'Upload my certificate',
    uploading: 'Reading the document…',
    noneYet: 'No certificate on file.',
    rejectedBecause: (reason: string): string => `Rejected: ${reason}`,
    todoReview: (n: number): string => (n === 1 ? '1 insurance certificate to review' : `${n} insurance certificates to review`),
    todoMissing: (n: number): string => (n === 1 ? '1 co-owner without valid insurance' : `${n} co-owners without valid insurance`),
  },
  nav: { home: 'Home', requests: 'Requests', documents: 'Documents', units: 'Units', parties: 'Co-owners', quoteParts: 'Quote-parts', finances: 'Finances', maintenance: 'Maintenance', governance: 'Meetings', reservations: 'Reservations', insurance: 'Insurance', automation: 'Automation', onboarding: 'Onboarding', settings: 'Settings', profile: 'My profile' },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    add: 'Add',
    optional: 'optional',
    none: 'None',
    back: 'Back',
    error: 'Something went wrong.',
    errorWithReference: (ref: string) => `An unexpected error occurred (reference ${ref}). Try again; if it persists, send this reference to Syndiqia support.`,
    forbidden: 'You do not have permission for this action.',
    notFound: 'Not found.',
    conflict: 'This change conflicts with existing records.',
    invalid: 'Some fields are invalid.',
  },
  home: {
    todo: 'To do',
    allDone: 'Nothing pending. 👍',
    reconcile: (n: number) => (n === 1 ? '1 bank transaction to reconcile' : `${n} bank transactions to reconcile`),
    callsDue: (n: number) => (n === 1 ? '1 contribution call to issue' : `${n} contribution calls to issue`),
    transferDue: (amount: string) => `Transfer ${amount} to the other funds`,
    budgetDraft: "This fiscal year's budget is not adopted yet",
    noBudget: 'No budget for the current fiscal year',
    noYear: 'Finances are not set up yet',
    noQuoteParts: 'No quote-part schedule in force',
    unitsWithoutOwner: (n: number) => (n === 1 ? '1 unit without a recorded owner' : `${n} units without a recorded owner`),
    arrears: 'Accounts in arrears',
    overdueTotal: (amount: string, n: number) => `${amount} overdue, ${n === 1 ? '1 unit' : `${n} units`}`,
    seeAll: 'See all',
    funds: 'Funds',
    syndicate: 'The syndicate',
    buildings: (n: number) => (n === 1 ? '1 building' : `${n} buildings`),
    units: (n: number) => (n === 1 ? '1 unit' : `${n} units`),
    coOwners: (n: number) => (n === 1 ? '1 co-owner' : `${n} co-owners`),
    quotePartsSince: (d: string) => `Quote-parts in force since ${d}`,
    ownerSpace: 'Your co-owner space (your account, documents, requests) is coming soon.',
  },
  unitsPage: { title: 'Units', quotePart: 'Quote-part', owners: 'Owners', balance: 'Balance', empty: 'No units. Add them in Settings.' },
  settings: {
    title: 'Syndicate settings',
    intro: 'Set up once, at onboarding: buildings and units. Day-to-day work happens in the other sections.',
    buildings: 'Buildings',
    addUnits: 'Add units',
    structure: 'Type of community',
    structures: {
      divided_coownership: 'Syndicate of co-owners (divided co-ownership)',
      npo_with_servitudes: "Owners' association (non-profit, servitudes)",
    },
    structureHint: "Set when the account is opened. It determines which rules apply (Civil Code and Bill 16, or the association's by-laws).",
    rulesTitle: 'Meeting rules',
    boardRules: 'Board meetings',
    boardQuorum: 'Board quorum (%)',
    boardQuorumHint: '50 = a majority of the directors',
    boardNotice: 'Notice to directors (days before)',
    boardCasting: 'The chair of the meeting has a casting vote in case of a tie',
    rulesIntro: "From the declaration of co-ownership or the association's by-laws. Used for the attendance register, the quorum and the notice of meeting.",
    rulesDefault: 'Defaults for this type of community: to confirm against your documents.',
    voteBasis: 'How votes are counted',
    voteBases: { quote_parts: 'By quote-parts', lots: 'One vote per lot', persons: 'One vote per member' },
    quorumPercent: 'Quorum (% of votes)',
    quorumHint: '50% = majority of votes (more than half); any other value = at least that percentage.',
    noticeMin: 'Notice: at least (days before)',
    noticeMax: 'Notice: at most (days before)',
    rulesSaved: 'Rules saved.',
    accessTitle: 'Access review',
    accessIntro: 'Who has access to this syndicate, with which roles. Review regularly (Law 25): remove access that is no longer needed.',
    lastSeen: (d: string): string => `Seen on ${d}`,
    neverSeen: 'Never signed in',
    flags: { never_used: 'Invitation never used', no_record: 'No linked record' },
    removeAccess: 'Remove access',
    confirmRemove: (name: string): string => `Remove ${name}'s access to this syndicate? Their account stays active elsewhere.`,
    boardRolesHint: "Board roles are removed from the person's page.",
    vendorLinks: "Vendors' active links",
    noVendorLinks: 'No active links.',
    vendorLink: (n: number, title: string): string => `WO #${n} — ${title}`,
  },
  requests: {
    attachments: 'Attachments',
    attach: 'Attach photos or PDFs',
    removeFile: 'remove',
    title: 'Requests',
    byEmail: (address: string) => `You can also write to ${address}: your email becomes a request, and you can reply to the board's messages by email.`,
    byEmailBoard: (address: string) => `Emails sent to ${address} become requests; replies by email are added to the right request. Give this address to the co-owners.`,
    new: 'New request',
    empty: 'No requests.',
    filters: { open: 'Open', all: 'All' },
    number: 'No.',
    subject: 'Subject',
    category: 'Type',
    categories: {
      maintenance: 'Repair or maintenance',
      question: 'Question',
      renovation: 'Renovation authorization',
      document: 'Document request',
      complaint: 'Complaint or nuisance',
      other: 'Other',
    },
    priority: 'Priority',
    urgent: 'Urgent',
    status: 'Status',
    statuses: { new: 'New', in_progress: 'In progress', waiting: 'Waiting for your reply', resolved: 'Resolved' },
    statusesBoard: { new: 'New', in_progress: 'In progress', waiting: 'Waiting for requester', resolved: 'Resolved' },
    unit: 'Unit',
    noUnit: 'Common areas / general',
    requester: 'Requester',
    onBehalfOf: 'On behalf of (request received by phone or email)',
    myself: 'Myself',
    description: 'Description',
    descriptionHint: 'Describe the situation: where, since when, photos to come…',
    updated: 'Updated',
    assignee: 'Assignee',
    unassigned: 'Unassigned',
    reply: 'Reply',
    replyPlaceholder: 'Your message…',
    internal: 'Internal note (hidden from the requester, never sent)',
    internalBadge: 'Internal note',
    send: 'Send',
    emailed: (to: string) => `Email to ${to}`,
    deliveryStatus: { queued: 'queued', sending: 'sending', sent: 'sent', failed: 'failed' },
    back: 'All requests',
    newCount: (n: number) => (n === 1 ? '1 new request' : `${n} new requests`),
    mine: 'My requests',
  },
  emails: {
    title: 'Sent emails',
    intro: 'Every email and its delivery status (proof of delivery).',
    capture: 'Demo mode: emails are recorded here but do not leave the server.',
    to: 'Recipient',
    empty: 'No emails.',
  },
  access: {
    title: 'Access',
    none: 'No access',
    invited: (d: string) => `Invited on ${d}`,
    active: (d: string) => `Active — seen on ${d}`,
    invite: 'Invite',
    reinvite: 'Resend the invitation',
    noEmail: 'Email required to invite',
    sent: 'Invitation sent.',
    emailNotSent: 'Access granted, but the invitation email could not be sent (email sending in test mode: only verified addresses receive it). Invite again once the address is verified.',
    confirm: (name: string) => `Invite ${name} to create their online access? They will receive an email to choose a password.`,
    myAccount: 'My account',
    myUnits: 'My units',
    noUnits: 'No unit is linked to your account.',
  },
  approvals: {
    title: 'Approvals',
    pending: 'To approve',
    none: 'Nothing waiting for approval.',
    requestedBy: 'Prepared by',
    system: 'Syndiqia (automatic)',
    on: 'on',
    approve: (n: number) => (n === 1 ? 'Approve sending (1 email)' : `Approve sending (${n} emails)`),
    approveOnly: 'Approve',
    reject: 'Decline',
    rejectReason: 'Reason (optional)',
    include: 'Include',
    preview: 'Preview',
    emails: 'Emails',
    escalations: 'Files for the board to review',
    escalationHelp: 'Two reminders had no effect. The board decides what comes next (formal notice, payment arrangement…). No email is sent.',
    unreachable: 'No email address — to reach another way',
    unreconciled: (n: number) => `Warning: ${n} bank deposit(s) not reconciled. Some co-owners may already have paid — reconcile the bank before approving.`,
    reconcile: 'Reconcile now',
    status: { pending: 'Pending', approved: 'Approved', rejected: 'Declined', cancelled: 'Cancelled' },
    result: (sent: number, skipped: number) => `${sent} email(s) sent${skipped ? `, ${skipped} dropped because paid in the meantime` : ''}.`,
    stage: { 1: 'First reminder', 2: 'Second reminder' } as Record<number, string>,
    canOnlyView: 'Only a board member can approve this.',
    todo: (n: number) => (n === 1 ? 'Reminder batch to approve (1 email)' : `Reminder batch to approve (${n} emails)`),
    prepare: 'Prepare payment reminders',
    nothing: 'No reminders to send for now.',
  },
  collections: {
    stage: { 1: 'First reminder sent', 2: 'Second reminder sent', 3: 'Referred to the board' } as Record<number, string>,
    escalated: (label: string) => `Collection to review: unit ${label}`,
  },
  documents: {
    title: 'Documents',
    empty: 'No documents.',
    upload: 'Add a document',
    file: 'File (PDF or text, 20 MB max.)',
    docTitle: 'Title',
    category: 'Category',
    categories: {
      declaration: 'Declaration of co-ownership',
      letters_patent: 'Letters patent',
      bylaws: 'By-laws',
      servitudes: 'Servitude deeds',
      minutes: 'Minutes',
      insurance: 'Insurance',
      contingency_study: 'Contingency fund',
      logbook: 'Maintenance logbook',
      financial_statements: 'Financial statements',
      notice: 'Notices',
      other: 'Other',
    },
    visibility: 'Visible to',
    visibilities: { members: 'All co-owners and occupants', board: 'The board only' },
    boardOnly: 'Board only',
    date: 'Document date',
    pages: (n: number) => (n === 1 ? '1 page' : `${n} pages`),
    open: 'Open',
    delete: 'Delete',
    confirmDelete: (title: string) => `Delete “${title}”? This cannot be undone.`,
    search: 'Search the documents…',
    results: 'Results',
    noResults: 'No passage found.',
    page: (n: number) => `page ${n}`,
  },
  suggestion: {
    title: 'Syndiqia suggestion',
    subtitle: "Draft prepared automatically from the syndicate's documents. Check it before sending.",
    suggested: 'Suggested type',
    apply: 'Apply',
    urgent: 'Suggested urgency',
    draft: 'Draft reply',
    sources: 'Sources',
    boardDecision: 'Board decision required',
    internalNote: 'Note for the board',
    use: 'Use this draft',
    dismiss: 'Dismiss',
    used: 'Draft used.',
    dismissed: 'Suggestion dismissed.',
    confidence: { high: 'high confidence', medium: 'medium confidence', low: 'low confidence' },
    page: (n: number) => `p. ${n}`,
  },
  ask: {
    title: 'A question about the rules?',
    placeholder: 'E.g.: Can I have a dog? When can renovation work be done?',
    ask: 'Ask',
    thinking: "Searching the syndicate's documents…",
    automatic: "Automatic answer from the syndicate's documents. It does not replace a board decision.",
    notCovered: 'The documents do not seem to cover this question.',
    forward: 'Forward my question to the board',
    forwarded: 'Question forwarded to the board: you can follow it under “Requests”.',
    sources: 'Sources',
    previous: 'My recent questions',
    shortcut: 'Ask a question about the rules',
  },
  buildings: {
    title: 'Buildings',
    empty: 'No buildings yet.',
    add: 'Add a building',
    name: 'Name',
    streetAddress: 'Address',
    city: 'City',
    postalCode: 'Postal code',
    units: 'Units',
  },
  units: {
    title: 'Units',
    empty: 'No units in this building.',
    add: 'Add a unit',
    label: 'Number',
    kind: 'Type',
    cadastralLot: 'Cadastral lot',
    floor: 'Floor',
    area: 'Area (m²)',
    kinds: { residential: 'Residential', commercial: 'Commercial', parking: 'Parking', storage: 'Storage', other: 'Other' },
  },
  ownership: {
    title: 'Owners',
    current: 'Current owners',
    none: 'No owner recorded.',
    record: 'Record a change of ownership',
    effectiveFrom: 'Effective from',
    owner: 'Owner',
    share: 'Undivided share',
    addOwner: 'Add an undivided co-owner',
    sharesMustSumToOne: 'Shares must add up to exactly 1.',
    since: 'since',
    noParties: 'First add people under “Co-owners”.',
  },
  parties: {
    title: 'Co-owners and contacts',
    empty: 'No one recorded yet.',
    add: 'Add a person or organization',
    kind: 'Type',
    kinds: { person: 'Person', organization: 'Organization' },
    displayName: 'Name',
    email: 'Email',
    phone: 'Phone',
    preferredLanguage: 'Communication language',
    languages: { fr: 'French', en: 'English' },
    boardOnly: 'Contact details are restricted to the board (Law 25).',
  },
  finances: {
    title: 'Finances',
    overview: 'Overview',
    budget: 'Budget',
    journal: 'Journal',
    bank: 'Bank',
    noYear: 'No fiscal year yet. Create the first one to set up the chart of accounts and the budget.',
    createYear: 'Create the fiscal year',
    yearLabel: 'Label',
    yearStart: 'Start',
    yearEnd: 'End (exclusive)',
    year: 'Fiscal year',
    funds: 'Funds',
    cash: 'Cash and investments',
    receivable: 'Receivable from co-owners',
    netAssets: 'Net assets',
    arrears: 'Accounts in arrears',
    noArrears: 'No accounts in arrears.',
    unit: 'Unit',
    owners: 'Owners',
    balance: 'Balance',
    current: 'Current',
    d30: '1–30 d',
    d60: '31–60 d',
    d90: '61–90 d',
    over90: '90 d +',
    creditBalance: 'Credit balance',
    callsDue: (n: number) => (n === 1 ? '1 contribution call to issue' : `${n} contribution calls to issue`),
    postCalls: 'Issue due calls',
    callsPosted: (n: number) => (n === 1 ? '1 call issued.' : `${n} calls issued.`),
  },
  budget: {
    status: { draft: 'Draft', adopted: 'Adopted' },
    noBudget: 'No budget for this fiscal year.',
    contributions: "Co-owners' contributions",
    otherRevenue: 'Other revenue',
    expenses: 'Expenses',
    total: 'Total',
    perMonth: 'per month',
    installments: 'Installments per year',
    edit: 'Edit the budget',
    save: 'Save draft',
    adopt: 'Adopt the budget',
    adoptConfirm: 'Once adopted, the budget can no longer be changed and contribution calls are scheduled. Continue?',
    calls: 'Contribution calls',
    due: 'Due',
    posted: 'Issued',
    scheduled: 'Scheduled',
    account: 'Account',
    fund: 'Fund',
    amount: 'Annual amount',
    onlyNonZero: 'Show entered amounts only',
  },
  journal: {
    title: 'General journal',
    date: 'Date',
    description: 'Description',
    kind: {
      opening: 'Opening',
      contribution_call: 'Contribution call',
      payment: 'Payment',
      expense: 'Expense',
      manual: 'Entry',
      reversal: 'Reversal',
    },
    debit: 'Debit',
    credit: 'Credit',
    reverse: 'Cancel with a reversing entry',
    reversed: 'Reversed',
    reason: 'Reason for the reversal',
    showLines: 'Details',
  },
  statement: {
    title: "Co-owner's account",
    empty: 'No transactions.',
    charge: 'Charged',
    payment: 'Paid',
    balance: 'Balance',
    byFund: 'Balance by fund',
    recordPayment: 'Record a payment',
    amount: 'Amount',
    date: 'Date',
    reference: 'Reference',
    noLedger: 'Accounting is not set up yet.',
  },
  bank: {
    title: 'Bank',
    import: 'Import a bank statement',
    account: 'Bank account',
    file: 'CSV file exported from the bank',
    mapping: 'File columns',
    skipRows: 'Header lines to skip',
    dateColumn: 'Date',
    dateFormat: 'Date format',
    descriptionColumn: 'Description',
    amountMode: 'Amounts',
    oneColumn: 'One column (deposits positive)',
    twoColumns: 'Two columns (withdrawals / deposits)',
    amountColumn: 'Amount',
    withdrawalColumn: 'Withdrawals',
    depositColumn: 'Deposits',
    decimalComma: 'Decimal comma (1 234,56)',
    preview: 'Preview',
    unreadable: (n: number) => `${n} unreadable line(s) — check the columns.`,
    doImport: 'Import',
    imported: (n: number, d: number) => `${n} transaction(s) imported${d ? `, ${d} already imported` : ''}.`,
    tabs: { unmatched: 'To reconcile', matched: 'Reconciled', ignored: 'Set aside' },
    none: 'No transactions.',
    confirmAll: (n: number) => `Confirm the ${n} reliable suggestion(s)`,
    confirmed: (n: number) => `${n} transaction(s) reconciled.`,
    confirm: 'Confirm',
    other: 'Other…',
    suggestion: 'Suggestion',
    paymentFor: (units: string) => `Payment — ${units}`,
    entryLink: (d: string) => `Existing entry: ${d}`,
    confidence: { high: 'reliable', medium: 'to check' },
    payUnits: "Co-owner's payment",
    units: 'Unit(s)',
    record: 'Record the payment',
    postTo: 'Book to account',
    post: 'Book',
    ignore: 'Set aside',
    ignoreReason: 'Why set this transaction aside?',
    deposit: 'Deposit',
    withdrawal: 'Withdrawal',
    dueToFunds: (amounts: string) => `The operating account holds ${amounts} for the other funds.`,
    transfer: 'Record the transfer',
  },
  quoteParts: {
    title: 'Quote-parts (relative values)',
    intro: 'Relative values from the declaration of co-ownership. They allocate common expenses and weight votes.',
    current: 'In force',
    none: 'No schedule in force.',
    unit: 'Unit',
    value: 'Value',
    percent: '%',
    total: 'Total',
    source: 'Source',
    from: 'From',
    to: 'to',
    history: 'History',
    status: { active: 'In force', draft: 'Draft' },
    new: 'New schedule',
    denominator: 'Denominator',
    effectiveFrom: 'In force from',
    sourcePlaceholder: 'Declaration of co-ownership, schedule…',
    remaining: 'Left to allocate',
    activate: 'Save and put in force',
    needUnits: 'First add units to the buildings.',
    sumMismatch: 'Values must add up to the denominator.',
  },
};

/**
 * Labels that differ for an owners' association (non-profit with servitudes): members rather than co-owners,
 * an association rather than a syndicate, a cost allocation set by the by-laws rather than quote-parts.
 */
export const associationLabels: Record<'fr' | 'en', (t: Messages) => Messages> = {
  fr: (t) => ({
    ...t,
    nav: { ...t.nav, parties: 'Membres', quoteParts: 'Répartition' },
    roles: { ...t.roles, owner: 'Propriétaire (membre)' },
    settings: { ...t.settings, title: 'Paramètres de l’association' },
    home: { ...t.home, syndicate: 'L’association', coOwners: (n: number) => (n === 1 ? '1 membre' : `${n} membres`) },
    parties: { ...t.parties, title: 'Membres et contacts' },
    insurance: { ...t.insurance, title: 'Assurances des membres', roles: { ...t.insurance.roles, owner: 'Membre' } },
    members: { ...t.members, back: 'Membres et contacts', roles: 'Rôles dans cette association', profileIntro: 'Vos coordonnées dans cette association. Le conseil s’en sert pour les avis et les communications.' },
    documents: { ...t.documents, visibilities: { ...t.documents.visibilities, members: 'Tous les membres et occupants' } },
    quoteParts: {
      ...t.quoteParts,
      title: 'Répartition des charges',
      intro: 'La part de chaque lot dans les charges communes, selon les règlements de l’association. Elle répartit les charges, et pondère les votes si les règlements le prévoient.',
    },
  }),
  en: (t) => ({
    ...t,
    nav: { ...t.nav, parties: 'Members', quoteParts: 'Cost shares' },
    roles: { ...t.roles, owner: 'Owner (member)' },
    settings: { ...t.settings, title: 'Association settings' },
    home: { ...t.home, syndicate: 'The association', coOwners: (n: number) => (n === 1 ? '1 member' : `${n} members`) },
    parties: { ...t.parties, title: 'Members and contacts' },
    insurance: { ...t.insurance, title: "Members' insurance", roles: { ...t.insurance.roles, owner: 'Member' } },
    members: { ...t.members, back: 'Members and contacts', roles: 'Roles in this association', profileIntro: 'Your contact details in this association. The board uses them for notices and communications.' },
    documents: { ...t.documents, visibilities: { ...t.documents.visibilities, members: 'All members and occupants' } },
    quoteParts: {
      ...t.quoteParts,
      title: 'Cost allocation',
      intro: "Each lot's share of the common expenses, per the association's by-laws. It allocates expenses, and weights votes if the by-laws say so.",
    },
  }),
};
